import type { RelatedEntityType } from '../types/correspondenceLogs.types'
import { environment } from './environment'

export const getAwsUrl = () => {
  return environment.awsConnectUrl
}

export const getAWSLoginUrl = () => {
  return environment.awsLoginUrl
}

export const getLoginCredentials = () => {
  return {
    loginURL: environment.loginUrlDomainDev,
    loginClientId: environment.loginUrlClientId,
    authProvider: environment.authProvider,
    applicationName: 'agent',
    environment: environment.environment,
  }
}

export const FIVE_SEC = 5000
export const LOGIN_URL_RESPONSE_TYPE = 'code'
export const LOGIN_URL_SCOPE = 'email openid phone'
export const LOGIN_URL_REDIRECT_URL = '/'
export const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
export const DECIMAL_REGEX = /^\d+(\.\d+)?$/
export const VIEW_OBJECT_REGEX =
  /^(?:.*\s*:\s*)*[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\s*$/
export const NUMBER_REGEX_WHILE_SENDING = /(\+?\d[\d\s-]*)/g
export const NUMBER_REGEX = /(?:\+)?\d+(?=\D*$)/g
export const EXTRACT_EMAIL_FROM_VIEW_OBJECT_REGEX =
  /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g
export const PHONE_REGEX = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/
export const AWS_PHONE_REGEX = /^1?(\d{3})(\d{3})(\d{4})$/
export const ZIPCODE_REGEX = /^\d{5}(?:-\d{4})?$/
export const ALPHA_NUMERIC_REGEX = /^[a-zA-Z0-9]*$/
export const FAX_REGEX = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/
export const DOMAIN_NAME_REGEX = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+com\/?$/
export const NEW_CO_SUBDOMAIN_REGEX =
  /^(https?:\/\/)?([a-zA-Z0-9-]+\.)*newco\.com$/
export const ALPHA_NUMERIC_WITH_UNDERSCORE_HYPHEN = /^[a-zA-Z0-9_-]*$/
export const ALPHA_NUMERIC_WITH_SPACES_UNDERSCORE_REGEX = /^[a-zA-Z0-9 _]*$/
export const URL_REGEX =
  /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})(\/[\w .-]*)*\/?([#?][\w.-]*)?$/
export const E_INCOME_LABEL_REGEX = /(eIncome details)/i

export const DYNAMIC_QUEUE_URL_ROUTE = 'manage-queue/dynamic-queue'

const PROTOCOL = '^(https?:\\/\\/)' // protocol
const DOMAIN_NAME = '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' // domain name  en.wikipedia.org
const IPV4_ADDRESS = '((\\d{1,3}\\.){3}\\d{1,3}))' // OR ipv4 address  192.168.1.1
const PORT = '(\\:\\d+)?' // port  :8080
const BROWSER_FORMAT = '([/?#][^\\s]*)?' // to allow browser's formatting
const PATH = '(\\/[-a-z\\d%_.~+]*)*' // path  /products
const QUERY_STRING = '(\\?[;&a-z\\d%_.~+=-]*)?' // query string  ?q=shoes&page=2
const FRAGMENT = '(\\#[-a-z\\d_]*)?$' // fragment locator  #section2

export const MIN_TABLE_HEIGHT = '19.3rem'

export const FULL_URL_REGEX = new RegExp(
  PROTOCOL +
    DOMAIN_NAME +
    IPV4_ADDRESS +
    PORT +
    BROWSER_FORMAT +
    PATH +
    QUERY_STRING +
    FRAGMENT,
  'i'
)
export const ZIP_CODE_REGEX = /^\d+$/
export const VARCHAR_WITH_50_CHAR_LIMIT_REGEX = /^.{0,50}$/
export const VARCHAR_WITH_100_CHAR_LIMIT_REGEX = /^.{0,100}$/
export const VARCHAR_WITH_255_CHAR_LIMIT_REGEX = /^.{0,255}$/
export const DAY_REGEX = /^(1[0-9]|2[0-9]|3[0-2]|[1-9])$/
export const NUMERIC_REGEX = /^[0-9]\d*$/
export const HEXCOLOR_REGEX = /^(#[0-9A-F]{6}|#[0-9A-F]{3})$/i
export const NUMERIC_REGEX_52_NUMBER_LIMIT = /^(0|[1-9]|[1-4][0-9]|5[0-2])$/
export const ALPHA_NUMERIC_WITH_ALLOW_SPACE = /^[a-zA-Z0-9 ]*$/
export const MONTHLY_VALUES = /^([1-9]|[12][0-9]|3[01])$/
export const WEEKLY_VALUES = /^([1-9]|[1-4][0-9]|5[0-2])$/

export const AUTH_PROVIDER = {
  COGNITO: 'COGNITO',
  MSAL: 'MSAL',
}

export const queueAssignedRolesList = [
  'Supervisor',
  'Case Manager',
  'PAP Specialist',
  'PA Specialist',
  'BV Specialist',
  'CSR Specialist',
  'Intake Specialist',
  'QC Specialist',
  'Distribution Coordinator',
]

export const NO_TENANT_HEADER_API = [
  '/clients',
  '/client_integration_config',
  '/general-settings',
  'permission-set/all',
  'dashboard/client_program_data',
  'search/get-data',
]

export const premitiveDataTypes = {
  string: { value: 'varchar', label: 'String' },
  number: { value: 'number', label: 'Number' },
  date: { value: 'date', label: 'Date' },
  time: { value: 'time', label: 'Time' },
  dateTime: { value: 'timestamp', label: 'Timestamp' },
  boolean: { value: 'boolean', label: 'Boolean' },
  float: { value: 'float', label: 'Decimal' },
  uuid: { value: 'uuid', label: 'UUID' },
  upload: { value: 'upload', label: 'Upload' },
  object: { value: 'object', label: 'Object' },
  category: { value: 'category', label: 'Category' },
  long: { value: 'long', label: 'Long' },
  text: { value: 'text', label: 'Text' },
  integer: { value: 'integer', label: 'Integer' },
}

export const NAMESPACES = {
  NOTES: 'Notes',
  CLIENT_VERBIAGE: 'Client :',
  PROGRAM_VERBIAGE: 'Program :',
  CASE_BASKET_SUBTEXT: 'Cases assigned to you',
  TASK_BASKET_SUBTEXT: 'Tasks assigned to you',
  CANNOT_ENABLE_DRAFT: 'Cannot enable draft records',
  ENTITY_DATA_MESSAGE: 'Manage Data for Specific Entities',
  INCORRECT_DEFAULT_VALUE: 'Not a valid value',
  CONFIRM: 'Confirm',
  SPLIT: 'Split',
  CUSTOM_SPLIT: 'Custom',
  RANGE_SPLIT: 'Range',
  CANCEL_BUTTON: 'Cancel',
  SUBMIT: 'Submit',
  CONSENT_BUTTON: 'Send Consent',
  CONSENT: 'Consent',
  RESEND_CONSENT_BUTTON: 'Resend Consent',
  EDIT_BUTTON: 'Edit',
  RESEND_BUTTON: 'Resend',
  REVOKE_BUTTON: 'Revoke',
  LOGOUT_BUTTON: 'Logout',
  SESSION_INACTIVITY_TIME_LEFT: 'Inactivity Timeout In',
  SESSION_LAST_LOGIN: 'Signed In At',
  MANDATORY_SESSION_TIME_LEFT: 'Max Session Remaining',
  LOGIN_BUTTON: 'Login',
  UPDATE_BUTTON: 'Update',
  PATIENT: 'PATIENT',
  PATIENT_KEY: 'Patient',
  PROVIDER_KEY: 'Provider',
  CONTACT_KEY: 'Contact',
  PAYER_KEY: 'Payer',
  ORGANIZATION_KEY: 'Organization',
  FACILITY_KEY: 'Facility',
  PHARMACY_KEY: 'Pharmacy',
  PROVIDER: 'PROVIDER',
  LOGIN_TO_CONTINUE: 'Please login to AWS Connect to initiate calling.',
  CALL_IN_PROGRESS: 'Another call is already in progress!',
  SAVE_BUTTON: 'Save',
  REASSIGN_BUTTON: 'Reassign',
  SAVE_AND_NEXT_BUTTON: 'Save and Next',
  REMOVE_BUTTON: 'Remove',
  DASHBOARD: 'Dashboard',
  DASHBOARD_TAGLINE: 'This is currently work in progress.. !!!!',
  PHONE_VALIDATION_MSG: 'Invalid phone number',
  EMAIL_VALIDATION_MSG: 'Invalid email input',
  ZIPCODE_VALIDATION_MSG:
    'Please enter a valid ZIP code in the format 12345 or 12345-6789.',
  WEBSITE_VALIDATION_MSG: 'Invalid URL input',
  ENTITIES: 'Entities',
  ENTER: 'Enter',
  BACKSPACE: 'Backspace',
  ENTITY: 'Manage Data for specific entities',
  NO_ENTITIES_PRESENT: 'No entities present',
  NO_BOOKMARK_ENTITIES_PRESENT: 'No bookmarked entities present',
  BOOKMARKED_ENTITIES: 'Bookmarked Entities',
  BOOKMARK_ENTITIES: 'Bookmark Entities',
  ACTIONS: 'Actions',
  ALL_ENTITIES: 'All Entities',
  VALID_TOAST: 'Please enter valid details!',
  REQUIRED_TOAST: 'Please fill in all required fields',
  REQUIRED_FIELD: 'This is a required field',
  REQUIRED: 'Required',
  NO_LAYOUT: 'Layout not created for this entity.',
  YES: 'Yes',
  NO: 'No',
  CONFIRM_CANCEL: 'Are you sure you want to cancel?',
  OVERVIEW: 'Overview',
  VALID_DATA: 'Please fill valid data',
  BACK_BUTTON: 'Back',
  ADD: 'Add',
  GENERATE: 'Generate',
  GENERATE_CONSENT: 'Generate Consent',
  FILE: 'File',
  INFORMATION: 'Information',
  UNASSIGN_CONFIRMATION: 'Are you sure you want to unassign this row?',
  TRIAGE_FORM_MERGING_IN_PROGRESS:
    'Triage form merging is in progress. Please refresh the tasks list to view the updated details.',
  VIEW: 'View',
  VIEW_KEY: 'view',
  EDIT: 'Edit',
  E_CONSENT_TOOLTIP: 'e-consent',
  EDIT_MODE: 'EDIT_MODE',
  VIEW_MODE: 'VIEW_MODE',
  EDIT_KEY: 'edit',
  CLONE: 'Clone',
  CLONE_KEY: 'clone',
  ENABLED: 'enabled',
  TOGGLE: 'toggle',
  CREATE: 'Create',
  CREATE_KEY: 'create',
  CASE_TOOLTIP_MESSAGE: 'Case cannot be created from here.',
  PRACTICE_TOOLTIP_MESSAGE: 'Practice cannot be created from here.',
  TASK_TOOLTIP_MESSAGE: 'Task cannot be created from here.',
  CONFIRM_ACCEPT_CALL_MESSAGE:
    'Are you sure you want to move to the interaction page, your current data will be lost?',
  EMPTY_MAXRTE_DATA: 'No data present',
  EMPTY_DATA_MULTILOOKUP: 'No data present, please assign data',
  ADD_MODE_MULTILOOKUP: 'Please create entry before assigning data',
  EMPTY_DATA_UNASSIGNED: 'No data present, please add data',
  CANCEL_TABS_MULTTI: 'cancel-multilookup-tabs',
  ADD_TABS_MULTI: 'add-multilookup-tabs',
  ASSIGN_TABS_MULTI: 'assign-multilookup-tabs',
  MAIL_SUCCESS: 'E-mail sent successfully',
  MAIL_ERROR: 'Failed to send mail',
  DO_NOT_USE: 'Do Not Use',
  // Queue Assignment
  OVERLAPPING_AGENTS_MSG:
    '! Overlapping assignments detected. Please modify the agent or duration to proceed.',
  INTAKE_FAX_QUEUE: 'Intake Fax Queue',
  NAME_LABEL: 'Name',
  NAME_KEY: 'name',
  ROLES_LABEL: 'Roles',
  AGENTS_LABEL: 'Agents',
  AGENTS_ASSIGNED_KEY: 'agent_label',
  ENTITY_ID_LABEL: 'Entity Id',
  CREATED_ON_LABEL: 'Created on',
  CREATED_ON_KEY: 'created_at',
  UPDATED_ON_LABEL: 'Last Updated',
  UPDATED_ON_KEY: 'modified_at',
  ENTITY_NAME_LABEL: 'Entity Name',
  LAYOUT_NAME_LABEL: 'Layout Name',
  LAYOUT_NAME_KEY: 'layout_name',
  ENTITY_NAME_KEY: 'entity_name',
  ENTITY_ID_KEY: 'entity_id',
  LAYOUT_ID_LABEL: 'Layout Id',
  LAYOUT_ID_KEY: 'layout_id',
  DESCRIPTION_LABEL: 'Description',
  MESSAGE_KEY: 'message',
  DESCRIPTION_KEY: 'description',
  STATUS_LABEL: 'Status',
  STATUS_KEY: 'status',
  RESOLVED_STATUS_KEY: 'resolved_status',
  ASSIGNED_GROUP_LABEL: 'Assigned Group',
  ASSIGNED_GROUP_KEY: 'group_name',
  ASSIGNED_GROUP_PASCAL: 'groupName',
  ASSIGNED_GROUP_SORT: 'assignGroup.name',
  LAST_UPDATED_LABEL: 'Last Updated',
  LAST_UPDATED_KEY: 'last_updated',
  MANAGE_QUEUE_LABEL: 'Manage Queue',
  MANAGE_QUEUES_LABEL: 'Manage Queues',
  MANAGE_QUEUE_TITLE: 'Please enter the info below to create the queue',
  CREATE_QUEUE_TABLE_DESCRIPTION:
    'Please set filters for your queue based on the selected filtering layout',
  CREATE_QUEUE_PROGRAM_CONFIG:
    'Select program to configure program-specific queues',
  CREATE_QUEUE_ASSIGN_NEW_GROUP: 'Create New',
  CREATE_QUEUE_TITLE: 'Create Queue',
  CREATE_NEW_QUEUE_LABEL: 'Create New Queue',
  AGENT_VISIBILITY: 'AGENT_VISIBILITY',
  QUEUE_VISIBILITY: 'QUEUE_VISIBILITY',
  EQUALS_BOOLEAN: 'EQUALS_BOOLEAN',
  DELETE: 'Delete',
  DELETE_KEY: 'delete',
  QUEUE_CREATION: 'Queue Creation',
  QUEUE_ASSIGNMENT: 'Queue Assignment',
  QUEUE_ASSIGNMENT_MSG:
    'Switch between options to assign roles or agents to the queue',
  ROLE: 'Role',
  START_DATE_QUEUE_COLUMN: 'Start Date',
  END_DATE_QUEUE_COLUMN: 'End Date',
  USERID_QUEUE_COLUMN: 'UserID',
  AGENT_QUEUE_COLUMN: 'Agent',
  USER_ID: 'User ID',
  EMAIL_ID_KEY: 'email_id',
  USER_ID_KEY: 'user_id',
  START_DATE: 'Start Date ',
  START_DATE_KEY: 'start_date',
  END_DATE: 'End Date',
  END_DATE_KEY: 'end_date',
  EDIT_QUEUE: 'Edit Queue',
  VIEW_QUEUE: 'View Queue',
  CANCEL_CHANGE_MSG: 'Are you sure you want to discard the changes?',
  SAVE_AS_DRAFT_CHANGE_MSG: 'Are you sure you want to save as draft?',
  CANCEL_THREAD_MSG: 'Are you sure you want to delete this note?',
  ENABLE_MSG: 'Are you sure you want to enable this record?',
  DISABLE_MSG: 'Are you sure you want to disable this record?',
  DELETE_MSG: 'Are you sure you want to delete this record?',
  REVIEW: 'Review',
  GO_TO_WORKFLOW: 'Go to Workflow',
  DISCARD_FAX_CONFIRMATION_MESSAGE:
    'Are you sure you want to discard this intake? This action is irreversible, and the intake cannot be attached to any case or worked on further.',
  VOID_FAX_CONFIRMATION_MESSAGE:
    'Are you sure you want to void this intake? This action is irreversible, and the intake cannot be attached to any case or worked on further.',
  DISCARD_INTERACTION_CONFIRMATION_MESSAGE:
    'Are you sure you want to discard this interaction? This action is irreversible, and the interaction cannot be attached to any case or worked on further.',
  DISCARD_LETTER_CONFIRMATION_MESSAGE:
    'Are you sure you want to discard this letter? The action is irreversible and the letter can not be printed later.',
  VOID_LETTER_CONFIRMATION_MESSAGE:
    'Are you sure you want to void this letter ? This action is irreversible and the letter cannot be printed later.',
  REDACTED: '****',
  EMPTY_CASE_MESSAGE:
    'No data present. Click on "Create New Case" button to add data.',
  SAVE_AS_DRAFT: 'Save as Draft',
  AVAILABILITY_TOOLTIP_MESSAGE: 'Mark your Availability',
  MARK_AVAILABILITY_MSG:
    'Your availability status is currently unavailable. Please mark yourself as available',
  SUBSCRIBE: 'subscribe',
  AGENT_AVAILABILITY: 'AgentAvailability',
  AGENT_OFFLINE: 'agent-offline',
  AGENT_ONLINE: 'agent-online',
  FULLNAME_ERROR_MSG:
    'Full name must include at least a first name and a last name.',
  VIEW_360: '360 View',
  VIEW_360_KEY: 'view360',
  CASES_ROUTED_FOR_EXCEPTION_PAP:
    'Case has now been routed for exception request process',
  CASES_ROUTED_FOR_EXCEPTION_APPEAL:
    'Case has now been routed for appeal request process',
  UPLOAD: 'Upload',
  CLOSE: 'Close',
  DOWNLOAD_ALL: 'Download All',
  VIEW_DOCUMENTS: 'View Documents',
  HEADER_FOR_UPLOAD: 'Select and upload the files of your choice',
  DOWNLOAD_ERROR: 'Error during download:',
  DELETED: 'DELETED',
  CHANGED: 'CHANGED',
  REMOVED: 'REMOVED',
  ZIP_FILE: 'zip_file',
  PREVIEW: 'preview',
  ATTACH: 'Attach',
  GET_WORK: 'Get Work',
  CASE_ASSIGNED_MESSAGE: 'You have been assigned a new Intake:',
  LETTER_ASSIGNED_MESSAGE: 'You have been assigned a new Letter',
  CASES_ASSIGNED_MESSAGE: 'You have been assigned a new Case:',
  CASE_UNASSIGNED_MESSAGE: 'You have been unassigned from:',
  ENTER_VALID_COMMA_SEPERATED_STRING:
    'Enter a valid comma-separated string of numbers',
  ENTER_VALID_RANGE: 'Enter a valid range',
  VALUES_CANNOT_BE_ZERO: 'Values cannot be zero.',
  INPUT_CANT_END_WITH_COMMA:
    'Input cannot end with a comma, cannot contain zeroes, and must be a valid number list.',
  EXCEEDS_TOTAL_NO_OF_PAGES: ' exceeds total number of pages i.e ',
  PLACEHOLDER_THREADS: 'Add a note',
  DISCARD_REASON_VALIDATION_MESSAGE:
    'Reason for discarding the fax is required.',
  VOID_REASON_VALIDATION_MESSAGE: 'Reason for voiding the fax is required.',
  DISCARD_INTERACTION_REASON_VALIDATION_MESSAGE:
    'Reason for discarding the interaction is required.',
  DISCARD_REASON_VALIDATION_MESSAGE_LETTER:
    'Reason for discarding the letter is required.',
  ERROR_PAGE_NUMBER_SINGULAR: 'Page number',
  ERROR_PAGE_NUMBER_PLURAL: 'Page numbers',
  ERROR_IS_INVALID: 'is invalid.',
  ERROR_ARE_INVALID: 'are invalid.',
  ERROR_PLEASE_ENTER_VALID_VALUES: 'Please enter values between 1 and',
  NO_REASON: 'No reason',
  NO_DATA: 'no value',
  VIEW_DOCUMENT: 'View Document',
  AT_RISK: 'at_risk',
  OVERDUE: 'overdue',
  CREATED_AT: 'created_at',
  // workbasket section
  MY_TASKS: 'My Tasks',
  MY_CASES: 'My Cases',
  PROCESSING_LOWERCASE: 'processing',
  PENDING_INTAKE_LOWERCASE: 'pending_intake',
  DENIED_COMPLETED_LOWERCASE: 'denied_completed',
  CASE_DOCUMENTS: 'case_documents',
  WEB_ENROLLMENT_DOCUMENT: 'web_enrollment_document',
  STANDARD_USER: 'Standard User',
  BV_COMPLETED: 'BV Completed',
  NOT_ENTERED: 'Not Entered',
  NOT_AVAILABLE: 'NOT AVAILABLE',
  NOT_AVAILABLE_UPPERACSE: 'NOT_AVAILABLE',
  NO_ACTIVE_PROGRAM_VALIDATION_MSG:
    'No active programs are currently assigned to this user.',
  DASHBOARD_ACCESS_RESTRICTED_TO_NON_STANDARD_USERS:
    'Dashboard access is restricted for users other than Standard users.',
  COMMUNICATION_ID_KEY: 'communication_id',
  USER_NAME_KEY: 'user_name',
  USERS_KEY: 'users',
  PROGRAM_ID_KEY: 'program_id',
  CASE_ID_KEY: 'case_id',
  CASE_KEY: 'case_key',
  TASK_ID_KEY: 'task_id',
  TASK_KEY: 'task_key',
  DASHBOARD_NO_LAYOUT_PRESENT:
    "No Layout present. Please Add it from Admin Portal and Ensure you're on correct client.",
  DASHBOARD_NO_DATA_PRESENT: 'No data present. Please add data.',
  DASHBOARD_NO_DATA_PRESENT_AND_NO_CREATE_PERMISSION:
    "No data present. You don't have permission to create ",
  RECORDS: ' records.',
  VIEW_RECORDS: 'View Records',
  PARENT_CASE_ID: 'parent_case',
  SEARCH: 'Search',
  CLEAR: 'Clear',
  COMPLETED_LOWERCASE: 'completed',
  COMMUNICATION_STATUS_LOWERCASE: 'communication_status',
  WEB_UPPERCASE: 'WEB',
  FAX_UPPERCASE: 'FAX',
  UNSAVED_CHANGES_MESSAGE:
    'You have unsaved changes. Do you want to proceed without saving?',
  BV_CANCELLED: 'BV Cancelled',
  CANCELLED: 'CANCELLED',
  COMPLETED_STEPPER: 'Completed',
  IN_PROGERSS_STEPPER: 'In Progress',
  PENDING_STEPPER: 'Pending',
  SEARCH_REQUESTER_INFORMATION: 'Search Requester Information',
  REVIEW_REQUESTOR: 'Review Requestor',
  SELECT_AND_CREATE_CASE: 'Select & Create Case',
  GET_MEMBER_ID: 'Get Member ID',
  GET_MEMBER_ID_AND_SAVE: 'Get Member ID and Save',

  // TASKS IN WORKFLOW
  MOVE_TO_MI: 'Move to MI',
  MISSING_INFORMATION: 'Missing Information',
  LOADING_PLACEHOLDER: 'Loading...',
  REVALIDATE: 'Revalidate',
  TASK_ID: 'Task ID',
  TASK_NAME: 'Task Name',
  DUE_DATE: 'Due Date',
  STATUS: 'Status',
  BULK_PRINT: 'Bulk Print',
  PRINT: 'Print',
  MARK_AS_COMPLETED: 'Mark as Completed',
  DISCARDED_LOWERCASE: 'discarded',
  WEB_ENROLLMENT_STATUS: 'web-enrollment-status',
  TASK_SUMMARY: 'Task Summary',
  ENROLLMENT_ID_KEY: 'enrollment_id',
  FAX_NAME: 'fax_name',
  SCRIPT_CLEANUP: '🧹 Script cleaned up',
  NO_ID_PROVIDED_FOR_EDITING: 'No ID provided for editing',
  NO_ENTITY_DATA_AVAILABLE_FOR_EDITING: 'No entity data available for editing',
  COMPLETED: 'COMPLETED',
  PENDING: 'PENDING',
  RESOLVED: 'RESOLVED',
  RELAY_RESULTS: 'Relay Results',
  SEARCH_RESULT: 'Search Result',
  BASIC_SEARCH: 'Basic Search',
  ADVANCED_SEARCH: 'Advanced Search',
  COMPLETION_DATE: 'Completion Date',
  ASSIGNEE: 'Assignee',
  UNASSIGNED: 'Unassigned',
  OUTCOME: 'Outcome',
  ENTITY_DROPDOWN: 'Entity',
  ENTITY_KEY: 'entity',
  ENTITY_TYPE: 'Entity Type',
  PERSON: 'Person',
  ACCOUNT: 'Account',
  ENTITY_TYPE_KEY: 'entity_type',
  SELECT_TASK: 'Select Task',
  TASK_TYPE: 'Task Type',
  SOMETHING_WENT_WRONG: 'Something went wrong!',
  RESEND_MEC_CONSENT_SUCCESS_MSG: 'Consent request resend successfully',
  REVOKE_MEC_CONSENT_SUCCESS_MSG: 'Consent request revoked successfully',
  RESEND_MEC_CONSENT_ERROR_MSG: 'Failed to resend consent request',
  REVOKE_MEC_CONSENT_ERROR_MSG: 'Failed to revoke consent request',
  TEXT_MSG_CONSENT_ERROR_MSG:
    'Cannot send text since no valid text messaging consent found for the case',
  TEXT_MSG_CONSENT_ERROR_TITLE: 'Text Messaging Consent Not Found',
  TASK_NOTES_SAVED: 'Task notes saved!',
  PLEASE_LOGIN_FIRST: 'Please login first before making a call',
  WORKFLOW_DEFINITION_DEPRECATED_LABEL: 'Workflow Definition Deprecated',
  GENERAL_CASE_INFORMATION: 'General Case Information',
  WORKFLOW_DEFINITION_DEPRECATED_DESCRIPTION:
    'This workflow definition has been deprecated and is no longer available.',
  ADD_TASK: 'Add Task',
  TASK_UPDATION_MSG_TO_MANUALLY_REFRESH:
    'It appears the task updates may not be updated properly.',
  FOLLOW_UP_CALL: 'Follow Up Call',
  STATUS_ERROR_MSG: 'Error fetching case statuses:',
  STATUS_NOT_COMPLETED: 'Not Completed',
  STATUS_COMPLETED: 'Completed',
  CASE_STATUSES: 'case_statuses',
  TASK_CANNOT_BE_COMPLETED: "Task can't be completed yet",
  TASK_CANNOT_BE_CANCELLED: "Task can't be cancelled!",
  TASK_COMPLETED_SUCCESSFULLY: 'Task completed successfully!',
  TASK_PENDING_SUCCESSFULLY: 'Task moved to Pending state successfully!',
  TASK_OPEN_SUCCESSFULLY: 'Task moved to Open state successfully!',
  TASK_CANCELLED_SUCCESSFULLY: 'Task cancelled successfully!',
  SOMETHING_WENT_WRONG_TASK_COULD_NOT_BE_COMPLETED:
    'Something went wrong, could not complete task!',
  SOMETHING_WENT_WRONG_TASK_COULD_NOT_BE_PENDING:
    'Something went wrong, could not move to pending state!',
  SOMETHING_WENT_WRONG_TASK_COULD_NOT_BE_OPEN:
    'Something went wrong, could not move to open state!',
  SOMETHING_WENT_WRONG_TASK_COULD_NOT_BE_CANCELLED:
    'Something went wrong, could not cancelled task!',
  SOMETHING_WENT_WRONG_STATUS_UPDATE:
    'Something went wrong while updating the status',
  UNKNOWN_MESSAGE: 'Unknown status',
  RE_VALIDATE: 'RE_VALIDATE',
  USER: 'USER',
  TRANSFER_TO_COPAY_VENDOR: 'Transfer to Copay Vendor',
  FILE_SIZE_EXCEEDS_MSG: 'File size exceeds the 5MB limit.',
  INVALIDE_FILE_TYPE:
    'Invalid file type. Supported formats: JPG, PNG, PDF, DOC, DOCX, DOCM, XLS, XLSX, XLSM, TXT, CSV',
  SENDER_NAME: 'sender_name',
  VALUE: 'value',
  SUFFIX: 'suffix',
  TASK: 'task',
  HEADERS: 'headers',
  SOURCE: 'source',
  COMPLETE_CASE: 'Complete Case',
  CASE_SUMMARY_INCOMPLETE_TOASTER: 'Please update the Case Summary',
  TASK_CANNOT_BE_COMPLETED_WITHOUT_GENERATING_DOC:
    'Task can not be completed without generating document',
  CLOSURE: 'closure',
  CASE_CLOSURE_NOTES: 'Case Closure Notes',
  VOID: 'void',
  CASE_VOID_NOTES: 'Case Void Notes',
  ADDED: 'ADDED',
  STATE: 'state',
  EINSURANCE_DETAILS: 'eInsurance Details',
  ELIGIBILITY_FOUND: 'Eligibility Found',
  PATIENT_EINSURANCE_DETAILS: 'Patient eInsurance Details',
  LABEL_KEY: 'label',
  GLOBAL: 'Global',
  GLOBAL_KEY: 'GLOBAL',
  CLIENT: 'Client',
  UNKNOWN: 'Unknown',
  HEADER: 'Header',
  RECORD_STATUS_LOWERCASE: 'record_status',
  ERROR_UPDATING_RECORD_STATUS: 'Error updating record status',
  RECORD_STATUS_CHANGED_TO_RECEIVED_SUCCESSFULLY:
    'Record status changed to Received successfully',
  OTHER_KEY: 'other',
  APPROVED: 'Approved',
  OFF_LABEL_HISTORICAL_DENY: 'OFF_LABEL_HISTORICAL_DENY',
  DIAGNOSIS: 'Diagnosis',
  PHARMACY_DETAILS_FOUND: `Pharmacy Details Found`,
  RESULTS_FOUND: ' Results Found',
  NO_RESULTS_FOUND: 'No Results Found',
  ADD_PATIENT: 'Add Patient',
  SAVE: 'Save',
  NO_PHARMACY_DETAILS_FOUND: 'No Pharmacy Details Found',
  DETAILS_UPDATED_SUCCESSFULLY: 'Patient updated successfully',
  PATIENT_FIRST_NAME: 'Patient First Name',
  PATIENT_LAST_NAME: 'Patient Last Name',
  PATIENT_DOB: 'Patient DOB',
  PHARMACY_ID: 'Pharmacy ID',
  ZIPCODE: 'Zipcode',
  ADDRESS_LINE_1: 'Address Line 1',
  ADDRESS_LINE_2: 'Address Line 2',
  EPR_PATIENT_REQUEST: 'EPR Patient Request',
  SUBMIT_MANUAL_OVERRIDE: 'Submit Manual Override',
  MAXRTE_DATA_FETCH_LOADER: 'MaxRTE data fetch in progress',
  MAXRTE_DATA_FETCH_SUB_LOADER:
    'System is retrieving required information. Please keep this window open until completion.',
  COULD_NOT_MOVE_TO_PENDING: 'This task cannot be moved to pending state.',
  C_PATIENT_ADDRESS: 'c_patient_addresses_c_patients',
  C_PATIENT_PHONE: 'c_patient_phones_c_patients',
  C_PATIENT_CONSENT: 'c_patient_consents_patient',
  C_CONTACT_PHONE: 'c_contact_phones_contact',
  C_CONTACT_ADDRESS: 'c_contact_addresses_contact',
  C_PROVIDER_CONTACT: 'c_provider_phones_c_providers',
  NO_PHONE_FOUND: 'No consent or phone number available',
  CONDITIONAL_KEY: 'conditional',
  ZIP_CODE: 'Zip Code',
  CITY: 'City',
  STATE_ADDRESS: 'State',
  PHONE_TYPE: 'Phone Type',
  PHONE_NUMBER: 'Phone Number',
  SAVE_ADDRESS: 'required to save Address.',
  SAVE_CONTACT_INFO: 'required to save Contact Information',
  TASK_CANNOT_BE_COMPLETED_WITHOUT_PHONE_NUMBER:
    'Task cannot be completed without a phone number',
  DOCUMENTS: 'documents',
  PHONE_NUMBER_AND_NOTES_MANDATORY_FOR_TASK_COMPLETION:
    'Phone number and Notes is mandatory to complete the task.',
  UNRESOLVED_FACILITY_POPUP_HEADER:
    'Provider’s Office Contact Required for Facility',
  ACTION_REQUIRED: 'Action Required',
  VOUCHER_UPDATED_SUCCESSFULLY: 'Voucher updated successfully',
  VOUCHER_ACCORDION: 'Add Voucher Information',
  VOUCHER_REQUIRED: 'Voucher # is required',
  MAX_LENGTH_20: 'Maximum 20 characters are allowed',
  QUICK_DENIED_POPUP_HEADER: 'Case is Quick Denied',
  QUICK_DENIED_POPUP_MESSAGE:
    'This case has successfully been resolved as Quick Denied, and no further action is required',
  PENDING_INTAKE_POPUP_HEADER:
    'Review Completed – Awaiting Other Party Submission',
  UNRESOLVED_DEDUPE_ENTITIES_POPUP_HEADER:
    'Please associate the following entities in the case before proceeding:',
  EXPORT_SUCCESSFUL: 'Export Completed Successfully',
  EXPORT_ERROR: 'The export could not be completed.',
  WARN: 'WARN',
  TASK_RECORD_LIMIT_EXCEEDED:
    'Too many records to display. Please refine your filters (e.g., Date, Status, Owner).',
  TASK_FETCH_ERROR:
    'Error fetching tasks. Please contact system administrator.',
}

export const GENERATE_CONSENT_NAMESPACES = {
  PROGRAM: 'Program',
  PRODUCT: 'Product',
  RECIPIENT_ENTITY_TYPE: 'Recipient Entity Type',
  REQUEST_CHANNEL: 'Request Channel',
  CONSENT_TYPE: 'Consent Type',
  PATIENT: 'Patient',
  PROVIDER: 'Provider',
  GENERATE_CONSENT_SUCCESS_MSG: 'Email sent successfully.',
  GENERATE_CONSENT_ERROR_MSG: 'Email could not be sent.',
  EMAIL_ADDRESS_IS_REQUIRED: 'Email address is required.',
  PATIENT_REPRESENTATIVE: 'Patient Representative',
  EMAIL: 'Email',
  PATIENT_FIRST_NAME: 'Patient First Name',
  PROVIDER_FIRST_NAME: 'Provider First Name',
  PROVIDER_LAST_NAME: 'Provider Last Name',
  PATIENT_REPRESENTATIVE_FIRST_NAME: 'Patient Rep First Name',
  PATIENT_REPRESENTATIVE_LAST_NAME: 'Patient Rep Last Name',
  PATIENT_REPRESENTATIVE_DOB: 'Patient Rep DOB',
  PATIENT_REPRESENTATIVE_CONTACT: 'Patient Rep Relationship',
  PATIENT_REPRESENTATIVE_EMAIL: 'Patient Rep Email Address',
  PATIENT_LAST_NAME: 'Patient Last Name',
  PATIENT_DOB: 'Patient DOB',
  PROVIDER_DOB: 'Provider DOB',
  PATIENT_EMAIL: 'Patient Email Address',
  PROVIDER_EMAIL: 'Provider Email Address',
  PROVIDER_NPI: 'Provider NPI',
  PROVIDER_ID: 'Provider ID',
  INVALID_EMAIL_MSG: 'Invalid email entered.',
  INVALID_EMAIL_PERSONA_MSG: 'Invalid email entered for the selected persona',
  PATIENT_REQUIRED: 'Patient Information is required for E Consent',
  PRODUCT_REQUIRED: 'Product is required for E Consent',
  PATIENT_PRODUCT_REQUIRED:
    'Patient Information and Product is required for E Consent',
}

export const WORKFLOW_OUTCOMES = {
  SUCCESSFUL: 'Successful',
  SUCCESSFUL_KEY: 'SUCCESSFUL',
  IN_PROGRESS_LABEL: 'In Progress',
  IN_PROGRESS_KEY: 'IN_PROGRESS',
  FAILED_LABEL: 'Failed',
  FAILED_KEY: 'FAILED',
  UNSUCCESSFUL: 'Unsuccessful',
  LEFT_VOICEMAIL: 'Left Voicemail',
  UNABLE_TO_LEAVE_VOICEMAIL: 'Unable to Leave Voicemail',
  HANG_UP: 'Hang Up',
  TRANSMISSION_IN_PROGRESS: 'Transmission In Progress',
  TRANSMISSION_IN_PROGRESS_LABEL: 'TRANSMISSION_IN_PROGRESS',
  TRANSMISSION_FAILED: 'Transmission Failed',
  TRANSMISSION_SUCCESSFUL: 'Transmission Successful',
}

const DEFAULT_NESTING_DEPTH = 18

export const SUPPORTED_NESTING_DEPTH = localStorage.getItem(
  'SUPPORTED_NESTING_DEPTH'
)
  ? Number(localStorage.getItem('SUPPORTED_NESTING_DEPTH'))
  : DEFAULT_NESTING_DEPTH

export const SORT_DIRECTION = {
  asc: {
    lowerCase: 'asc',
    upperCase: 'ASC',
  },
  desc: {
    lowerCase: 'desc',
    upperCase: 'DESC',
  },
}

export const QUEUE_ASSIGNMENT = {
  roles: { key: 'roles', label: 'ROLE' },
  agents: { key: 'agents', label: 'AGENT' },
  QUEUE_ASSIGNMENT_TITLE: 'Queue Assignment',
}

export const INTAKE_QUEUE_TABS = {
  fax: { key: 'fax', label: 'Fax' },
  web: { key: 'web', label: 'Web' },
  email: { key: 'email', label: 'Email' },
}
export const STRING_CONSTANTS = {
  AWS_CONTAINER_NAME: 'ccp',
}

export const FILE_INPUT_TYPES = {
  NUMBER: 'number',
  DATE: 'date',
  DATE_TIME_LOCAL: 'datetime-local',
  TIME: 'time',
  EMAIL: 'email',
}

export const MODE_CONSTANTS = {
  ADD: 'add',
  EDIT: 'edit',
  VIEW: 'view',
  HIDDEN: 'hidden',
  VIEW_360: 'view360',
} as const

export const ACTIONS = {
  ADD: 'add',
  EDIT: 'edit',
  VIEW: 'view',
  TOGGLE: 'toggle',
} as const

export const LAYOUT_TYPE_NAMESPACE = {
  VIEW_EDIT_LABEL: 'Default View & Edit',
  LAYOUT_LISTING_LABEL: 'Default List View',
  OVERVIEW_LABEL: 'Default Overview',
  DEFAULT_VIEW_EDIT_LABEL: 'Default Edit Mode',
  VIEW_EDIT: 'EDIT_MODE',
  DEFAULT_VIEW_EDIT: 'EDIT_MODE',
  VIEW_EDIT_TYPE: 'EDIT_MODE',
  LAYOUT_LISTING: 'LISTING_MODE',
  DEFAULT_LAYOUT_LISTING: 'LISTING_MODE',
  LAYOUT_LISTING_TYPE: 'LISTING_MODE',
  OVERVIEW_TYPE: 'VIEW_MODE',
  DEFAULT_OVERVIEW: 'VIEW_MODE',
  VIEW_360_TYPE: 'LAYOUT_360',
  OVERVIEW: 'overview',
  TABS_TYPE: 'TABS_OVERVIEW',
  DEFAULT_OVERVIEW_LAYOUT: 'Default Overview',

  // custom layout types
  CUSTOM_EDIT_MODE: 'CUSTOM_EDIT_MODE',
  CUSTOM_LISTING_MODE: 'CUSTOM_LISTING_MODE',
  CUSTOM_VIEW_MODE: 'CUSTOM_VIEW_MODE',
}

export const CUSTOM_LAYOUTS_KEYS_LIST = [
  LAYOUT_TYPE_NAMESPACE.CUSTOM_EDIT_MODE,
  LAYOUT_TYPE_NAMESPACE.CUSTOM_LISTING_MODE,
  LAYOUT_TYPE_NAMESPACE.CUSTOM_VIEW_MODE,
]

export const DATE_RESTRICTION_TYPES = {
  FUTURE: 'future',
  PAST: 'past',
}

export const componentNames = {
  SINGLE_LINE: 'single line',
  MULTI_LINE: 'multi line',
  CHECKBOX: 'check box',
  RADIO_GROUP: 'radio',
  NUMBER: 'number',
  DATE: 'date',
  DATE_TIME: 'date-time',
  TIME: 'time',
  TOGGLE_SWITCH: 'toggle switch',
  DROPDOWN: 'drop down',
  HEADER: 'Header',
  PARAGRAPH: 'Paragraph',
  DECIMAL: 'decimal',
  ADDRESS: 'Address',
  NAME: 'Name',
  PHONE_NUMBER: 'phone number',
  EMAIL: 'email',
  FILE_UPLOAD: 'upload',
  MULTI_FILE_UPLOAD: 'upload',
  IMAGE_UPLOAD: 'image upload',
  AUDIO_VIDEO_UPLOAD: 'audio/video upload',
  DESCRIPTION: 'description',
  SLIDER: 'slider',
  RATING: 'rating',
  WEBSITE: 'website',
  URL: 'url',
  UNIQUE_ID: 'unique id',
  SSN: 'ssn',
  CURRENCY: 'currency',
  SINGLE_LOOKUP: 'single lookup',
  LINKED: 'linked',
  COMBOBOX: 'combo box',
  MULTI_LOOKUP: 'multi lookup',
  NPI: 'npi',
  DEA: 'dea',
  SERIAL: 'serial',
  MULTI_SELECT_DROPDOWN: 'multiselect dropdown',
  CATEGORY: 'category',
  FAX_NUMBER: 'fax',
  COMPUTED: 'computed',
  ZIP_CODE: 'zipcode',
  SINGLE_LOOKUP_CLICK: 'single lookup click',
} as const

export const ROW_TYPES = {
  NOTES: 'Thread',
  NOTE: 'thread',
  THREAD: 'Threads',
  SINGLE_LOOKUP_LISTING: 'single_lookup_listing',
  COMBOBOX: 'fun-notes',
  SUB_FORM: 'sub-form',
  MULTI_LOOKUP: 'lookup_listing',
}

export const EXTRA_FIELDS = {
  EXTRA_FIELD: '_unnecessaryKey_',
}

export const HTTP_STATUS_CONSTANTS = {
  SUCCESS: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZE: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  SERVER_ERROR: 500,
  CONFLICT: 409,
  NO_DATA: 0,
  STATUS_CODE_300: 300,
}

export const TABLE_ACTIONS = {
  REMOVE: 'remove',
  EDIT: 'edit',
  RESEND: 'resend',
  REVOKE: 'revoke',
}

export const MEC_RECORD_STATUSES = {
  COMPLETED: 'Completed',
  REQUESTED: 'Requested',
  REVOKED: 'Revoked',
  EXPIRED: 'Expired',
}

export const COLUMN_ACTIONS = {
  SELECTED: 'selected',
}

export const QUEUE = {
  INTAKE_QUEUE: 'Intake Queue',
}

export const INTAKE_QUEUE = {
  FAX: 'Fax',
  WEB: 'Web',
}

export const REGEX_ERR_MSG = {
  FIFTY: 'Exceeded fifty words limit',
  TWO_HUNDRED_FIFTY: 'Exceeded two hundred and fifty words limit',
}

export const BUTTONS = {
  SAVE: 'Save',
  SAVE_AS_DRAFT: 'Save as Draft',
  BACK: 'Back',
  CREATE_NEW_CASE: 'Create New Case',
  CANCEL: 'Cancel',
  SEND_TO_BV_QUEUE: 'Send to BV Queue',
  ADD_WITH_ICON: '+ Add',
  EDIT: 'Edit',
  CLEAR: 'Clear',
  PREVIEW: 'Preview',
  YES: 'Yes',
  NO: 'No',
  SEND: 'Send',
  RESEND: 'Resend',
  SEND_VIA_DOCUSIGN: 'Send via DocuSign',
  UPDATE: 'Update',
  SPLIT_FAX: 'Split fax',
  SPLIT_MAIL: 'Split mail',
  SEARCH: 'Search',
  DISCARD: 'Discard',
  SAVE_FILTER: 'Save Filter',
  UPDATE_FILTER: 'Update Filter',
  SAVE_AS: 'Save as',
  CREATE: 'Create',
  SEND_SMS: 'Send SMS',
}
export const MODAL_TYPES = {
  LINK: 'link',
  EDIT: 'edit',
  VIEW: 'view',
  DIALOG: 'dialog',
  ICON: 'icon',
  CANCEL: 'cancel',
  DRAFT: 'draft',
  TEMPLATE_TYPE_CHANGE: 'template_type_change',
}

export const WORKFLOW_MODAL_TYPES = {
  PENDING_INTAKE: 'PENDING_INTAKE',
  QUICK_DENIAL: 'QUICK_DENIAL',
  UNRESOLVED_DEDUPE: 'UNRESOLVED_DEDUPE',
} as const

export const REDIRECTING_TO_DOCUSIGN = {
  REDIRECTING_TO_DOCUSIGN: 'Redirecting to DocuSign...',
  SECRET_KEY: 'your-secure-key',
  DECRYPTION_RESULTED_IN_EMPTY_DATA: 'Decryption resulted in empty data',
  AN_UNKNOWN_ERROR_OCCURED_DURING_DECRYPTION:
    'An unknown error occurred during decryption.',
  DECRYPTION_FAILED: 'Decryption failed',
  MISSING_REQUIRED_URL_AND_OR_IV:
    'Missing required URL parameters: data and/or IV.',
  ERROR: 'Error',
  DECRYPTING_AND_REDIRECTING: 'Decrypting and Redirecting...',
  DATA: 'data',
  IV: 'iv',
  _ERROR: 'error',
  SUCCESS: 'success',
  REDIRECTING_LINK_ADDED_SUCCESSFULLY: 'Redirecting link added successfully',
  FAILED_TO_ADD_REDIRECTING_LINK: 'Failed to add redirecting link',
}
export const NAMESPACES_MANUAL_COMMUNICATION = {
  //Send Manual Communication
  GENERATE_COMMUNICATION_MESSAGE:
    'Click to Generate Communication Message & Documents',

  GENERATE_COVERSHEET_PREVIEW_MESSAGE: 'Click to Generate Coversheet preview',
  DISCARD_CHANGES_MSG: 'Are you sure to want discard the changes',
  SAVE_CHANGES_MSG: 'Are you sure to want to save changes',
  USER_ACCESS_CONTROL: 'User Access Control',
  CHOOSE_ROLE_PERMISSION_TAB:
    'Begin by choosing the appropriate tabs for the role to configure permissions.',
  NOT_ABLE_TO_DELETE_CONTAINER:
    'Please delete the fields from the container first, before deleting the container',
  REMOVE_LOOKUP_FIELDS_ERROR:
    'Please remove all related lookup fields before deleting lookup',
  VALID_EMAIL_ADDRESS: 'Please enter a valid email address',
  VALID_SMS_ADDRESS: 'Please enter a valid sms number',
  VALID_FAX_ADDRESS: 'Please enter a valid fax number',
  ADD: 'Add',
  EDIT: 'Edit',
  CANCEL: 'Cancel',
  SAVE: 'Save',
  OK: 'Ok',
  SPLIT_FAX: 'Split Fax',
  FAX: 'FAX',
}

export const APPLICATION_PREFERENCES = {
  GENERAL_SETTINGS: 'General Settings',
  CASE_ASSIGNMENT_CONFIGURATION: 'Case Assignment Configuration',
  CANCEL: 'Cancel',
  UPDATE: 'Update',
  SAVE: 'Save',
  SHIFT_TIME_ZONE: 'Shift Time Zone',
  SHIFT_TIMINGS: 'Shift Timings',
  CLEAR: 'Clear',
  ADD_SHIFT: '+ Add Shift',
  SHIFT: 'Shift',
  START_TIME: 'Start Time',
  END_TIME: 'End Time',
  EFFECTIVE_START_DATE: 'Effective Start Date',
  EFFECTIVE_END_DATE: 'Effective End Date',
  EDIT: 'edit',
  RENAME_THIS_SHIFT: 'Rename this shift',
  ENABLED: 'is_enabled',
  TOGGLE: 'toggle',
  MAXIMUM_CASES: 'Maximum Cases per Agent',
  MINIMUM_CASES: 'Minimum Cases per Agent',
  TIME_ZONE: 'Time Zone',
  AGENT_SCHEDULE_SETTINGS: 'Agent Schedule Settings',
  AGENT_SCHEDULE_DESCRIPTION: 'Manage agent shifts and schedules',
}

export const FAX_HEADER_ACTIONS = {
  CREATE: 'create',
  REVIEW: 'review',
  VIEW: 'view',
}

export const MAXRTE_SUPPORTED_TABLE_LAYOUT_IDS = [5809, 1963]

export const ENTITY_NAME = {
  CASE: 'Case',
  FACILITY: 'Facility',
  PROVIDER: 'Provider',
  PHARMACY: 'Pharmacy',
  ORGANIZATION: 'Organization',
  TASK: 'my_tasks',
  COMMUNICATION: 'communication',
  CASE_LOWER: 'case',
  C_CASES: 'c_cases',
  PATIENT: 'patient',
  REQUESTOR_VIEW: 'requestor_view',
  CASE_DOCUMENTS_LOWER: 'case_documents',
  HCP_PORTAL_ENTITY: 'hcp_portal_entity',
  USERS_DASHBOARD: 'users_dashboard',
  TASK_DASHBOARD: 'task_dashboard',
  MISSING_INFO_DASHBOARD: 'missing_info_dashboard',
  COMM_TYPE_LETTER: 'letter',
  PATIENTS: 'patients',
  CONSENT_REQUESTS_ENTITY_NAME: 'consent_requests',
  HCP_PORTAL_ENTITY_NAME: 'hcp_portal_entity_cases',
  PROVDERS: 'providers',
  CONTACTS: 'contacts',
  MIGRATED: 'migrated',
  MIGRATED_LAYOUT_NAME: 'Migrated Case',
  CASE_ENTITY_ID: '32',
  C_PATIENT_INSURANCES: 'c_patient_insurances_patient',
  C_PATIENT_ADDRESS: 'c_patient_addresses_c_patients',
  C_PATIENT_CONSENT: 'c_patient_consents_patient',
  PROVIDER_VIEW_KEY: 'providers_view',
  PATIENT_ENROLLMENT_VIEW_KEY: 'patient_enrollment_view',
  CASE_PAP_DETAILS: 'case_pap_details',
  C_PATIENT_EINSURANCE_DETAILS: 'c_patient_einsurance_details',
  PATIENT_EINSURANCE_DETAILS: 'patient_einsurance_details',
  CASE_VIEW_KEY: 'cases_entity_search_view',
  PHARMACY_VIEW_KEY: 'pharmacy_view',
  FACILITIES_VIEW_KEY: 'facilities_view',
  SERVICES: 'services',
  SERVICE_NAME_KEY: 'service_name',
  TASK_ENTITY: 'task-entity',
  PRACTICE_LOWER: 'practice',
  TASK_LOWER: 'task',
  HEALTHCARE_FACILITY: 'Healthcare Facility',
  C_AE_PC_DOCUMENT: 'c_ae_pc_document',
  AE_PC_DOCUMENT: 'ae_pc_document',
  EMBEDDED_DASHBOARD_DATA_MV: 'embedded_dashboard_data_mv',
}

export const ENTITY_FIELDS = {
  case: {
    communication: 'communications_communication_cases',
    case_pap_details: 'case_pap_details',
    patient: 'patient',
    c_case_income_details_case: 'c_case_income_details_case',
    c_medical_expense_details_case: 'c_medical_expense_details_case',
    c_case_shipment_address_case: 'c_case_shipment_address_case',
  },
  CASE_PAP_DETAILS: {
    acceptable_application_version: 'accepatble_application_version',
    icd_code: 'icd_code',
    insurance_status: 'insurance_status',
    insurance_type: 'insurance_type',
    va_benefits_applied: 'va_benefits_applied',
    medicaid_status: 'medicaid_status',
    household_size_application: 'household_size_application',
    fpl_percentage_application: 'fpl_percentage_application',
    annual_income_on_application: 'annual_income_on_application',
    household_size_poi: 'household_size_poi',
    annual_income_poi: 'annual_income_poi',
    fpl_percentage_poi: 'fpl_percentage_poi',
    fpl_percentage_final: 'fpl_percentage_final',
    household_size_final: 'household_size_final',
    annual_income_final: 'annual_income_final',
    products: 'products',
    PAP_ELIGIBILITY_ASSESSMENTS: 'pap_eligibility_assessments',
    PAP_ELIGIBILITY_ASSESSMENT_DETAILS:
      'c_pap_eligibility_assessment_details_eligibility_assessment',
    INDICATE_THIRD_PARTY_INSURANCE: 'indicate_third_party_insurance',
    patient_prescription_coverage: 'patient_prescription_coverage',
    employer_supplied_or_private_drug_coverage:
      'employer_supplied_or_private_drug_coverage',
    va_or_military_benefits: 'va_or_military_benefits',
    medicare_advantage: 'medicare_advantage',
    medicare_part_d: 'medicare_part_d',
    medicare_part_b: 'medicare_part_b',
    medicaid_prescription_drug_coverage: 'medicaid_prescription_drug_coverage',
    lis_extra_help: 'lis_extra_help',
    part_d_plan: 'part_d_plan',
    fulfillment_status: 'fulfillment_status',
    inflight: 'inflight',
    shipment: 'shipment',
  },
  c_case_income_details_case: {
    annual_amount: 'annual_amount',
  },
  c_medical_expense_details_case: {
    amount: 'amount',
  },
  c_case_shipment_address_case: {
    address_line_1: 'address_line_1',
    address_line_2: 'address_line_2',
    ship_to_entity: 'ship_to_entity',
    city: 'city',
    state: 'state',
    zipcode: 'zipcode',
  },
}

export const ENTITY_FIELDS_META = {
  CASE_PAP_DETAILS: {
    insurance_status: {
      options: [
        { value: 'Has Insurance', label: 'Has Insurance' },
        { value: 'No Insurance', label: 'No Insurance' },
      ],
    },
    patient_prescription_coverage: {
      options: [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
      ],
    },
    indicate_third_party_insurance: {
      options: [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
      ],
    },
    insurance_type: {
      option: [
        {
          label: 'Medicare Part B without supplemental/secondary Insurance',
          value: 'Medicare Part B without supplemental/secondary Insurance',
        },
        {
          label: 'Medicare Part B with supplemental/secondary Insurance',
          value: 'Medicare Part B with supplemental/secondary Insurance',
        },
        { label: 'Medicare Part D', value: 'Medicare Part D' },
        { label: 'Medicare Part A', value: 'Medicare Part A' },
        {
          label: 'Medicare Advantage',
          value: 'Medicare Advantage',
        },
        { label: 'Commercial', value: 'Commercial' },
        { label: 'VA Benefits', value: 'VA Benefits' },
        { label: 'Medicaid', value: 'Medicaid' },
      ],
    },
  },
}

export const ORDER_STATUS = {
  ORDER_NOT_STARTED: 'ORDER NOT STARTED',
  PRESCRIPTION_RECEIVED: 'PRESCRIPTION RECEIVED',
  UNDER_REVIEW: 'UNDER REVIEW',
  READY_TO_SHIP: 'READY TO SHIP',
  SHIPPED: 'SHIPPED',
  CANCELLED: 'CANCELLED',
}

export const COMMUNICATION_STATUSES = {
  PENDING_DISPATCH: 'Pending dispatch',
  PROCESSING_PRINTING: 'Processing Printing',
  DISCARDED: 'Discarded',
  COMPLETED: 'Completed',
}

export const ENTITY_IMPORTANT_KEYS = {
  CASE_ID: 'case_id',
  CASE_KEY: 'case_key',
  TASK_ID: 'task_id',
  ID: 'id',
}

export const WEBSOCKET_ACTIONS = {
  LOCK: 'lock',
  SUBSCRIBE: 'subscribe',
  AGENT_LIST: 'agent-list',
  UNLOCK: 'unlock',
  CONNECTION_DATA: 'connection-data',
  DISCONNECT: '$disconnect',
  ONLINE: 'online',
  OFFLINE: 'offline',
  ASSIGN: 'assign',
  UNASSIGN: 'unassign',
}
export const WBSOCKET_TOPIC = {
  INTAKE_QUEUE: 'IntakeQueue',
  AGENT_AVAILABILITY: 'AgentAvailability',
}

export const VIEW_360_CONSTANTS = {
  MANYTOMANY: 'Many-to-Many',
  MANYTOONE: 'Many-to-One',
  ONETOONE: 'One-to-One',
  ONETOMANY: 'One-to-Many',
  ROOT: 'root',
  TWENTYSIXTY: 'twentysixtytwenty',
  TAB: 'TABS_OVERVIEW',
}

export const correspondenceLogsPage = {
  IS_EMPTY_NULL: 'IS_EMPTY_NULL',
  IS_NULL: 'IS_NULL',
  IS_NOT_EMPTY_NOT_NULL: 'IS_NOT_EMPTY_NOT_NULL',
  IS_NOT_NULL: 'IS_NOT_NULL',
  CONTAINS: 'CONTAINS',
  CASE_ID: 'Case ID',
  CASE_ID_KEY: 'case_id',
  C_CASE: 'c_case',
  CASE_KEY: 'case_key',
  ENROLLMENT_ID: 'Enrollment ID',
  ENROLLMENT_ID_KEY: 'enrollment_id',
  CASES_CASE_KEY: 'cases.case_key',
  CASES_SERVICES: 'cases.services',
  CHANNEL_EXPORT_KEY: 'category_communication_type_id',
  CASES_SERVICES_KEY: 'cases.services',
  CASES: 'CASES',
  COMMUNICATION_ID: 'Communication ID',
  COMMUNICATION_ID_KEY: 'communication_id',
  REVIEWER: 'Reviewer',
  ASSIGNED_TO: 'Assigned to',
  MESSAGE: 'Message',
  MESSAGE_KEY: 'body',
  ID: 'ID',
  ID_KEY: 'id',
  CASE_ID_KEY_UPPERCASE: 'CASE_ID',
  CASE_ID_KEY_CAMELCASE: 'caseId',
  TEMPLATE: 'Template',
  TEMPLATE_KEY: 'template_name',
  TEMPLATE_KEY_UPPERCASE: 'TEMPLATE_NAME',
  PROGRAM: 'Program',
  PROGRAM_KEY: 'program',
  PROGRAM_EXPORT_KEY: 'programs_id.operationalProgramName',
  PRODUCT_EXPORT_KEY: 'product', // NOTE: Changed from product.trademarkedName, EP-5388
  RECIPIENT_TYPE_EXPORT_KEY: 'communication_recipient.category',
  RECIPIENT_EXPORT_KEY: 'communication_recipient.record_key',
  AGENT_NAME_EXPORT_KEY: 'users_id.firstName',
  PRODUCT: 'Product',
  PRODUCT_KEY: 'product',
  RECIPIENT_TYPE: 'Recipient Type',
  RECIPIENT_TYPE_SORT_KEY: 'recipients.category',
  RECIPIENT_SORT_KEY: 'recipients.record_id',
  RECIPIENT_TYPE_KEY: 'recipient_type',
  RECIPIENT: 'Recipient',
  RECIPIENT_ID: 'Recipient ID',
  RECEIVER: 'receiver',
  SENT_TO: 'Sent To',
  NOT_AVAILABLE: 'NOT_AVAILABLE',
  RECIPIENT_KEY: 'recipient',
  AGENT_NAME: 'Agent Name',
  AGENT_NAME_KEY: 'agent_name',
  ASSIGNED_TO_SORT_KEY: 'assignedTo', //NOTE: Changed from assigned_to.first_name
  SENDER_NAME: 'Sender',
  SENDER_NAME_KEY: 'sender_name',
  SENT_AT: 'Sent At',
  SENT_AT_KEY: 'sent_at',
  PROGRAM_KEY_OPERATIONAL: 'programs_id.operationalProgramName',
  PROGRAM_KEY_SORT: 'program.operationalProgramName',
  PROGRAM_KEY_UPPERCASE: 'PROGRAM',
  SERVICE: 'Service',
  CASE_TYPE: 'Case Type',
  CASE_TYPE_KEY: 'case_type',
  SERVICE_KEY: 'service',
  SERVICE_SORT_KEY: 'cases.services.serviceName',
  SERVICE_KEY_UPPERCASE: 'SERVICE',
  DATE_AND_TIME: 'Date & Time',
  RECEIVED_FROM: 'Received From',
  RECEIVED_FROM_UPPERCASE: 'RECEIVED_FROM',
  SENDER_NAME_UPPERCASE: 'SENDER_NAME',
  REQUESTOR_TYPE: 'Requestor Type',
  PATIENT_FIRST_NAME: 'Patient First Name',
  PATIENT_LAST_NAME: 'Patient Last Name',
  PRESCRIBER_FIRST_NAME: 'Prescriber First Name',
  PRESCRIBER_LAST_NAME: 'Prescriber Last Name',
  FACILITY_NAME: 'Facility Name',
  QUICK_DENIAL_REASON: 'Denial Reason',
  RECEIVED_FROM_KEY: 'senderName',
  RECEIVED_FROM_NEW_KEY: 'sender_name',
  RECEIVED_FROM_KEY_LOWER: 'received_from',
  PATIENT_FIRST_NAME_KEY: 'patient_first_name',
  REQUESTOR_TYPE_KEY: 'requestor_type',
  PATIENT_LAST_NAME_KEY: 'patient_last_name',
  QUICK_DENIAL_REASON_KEY: 'quick_denial_reason',
  PRESCRIBER_FIRST_NAME_KEY: 'prescriber_first_name',
  PRESCRIBER_LAST_NAME_KEY: 'prescriber_last_name',
  FACILITY_NAME_KEY: 'facility_name_new',
  RECEIVED_AT: 'Received At',
  RECEIVED_AT_KEY: 'received_at',
  CREATED_AT_KEY: 'created_at',
  RECEIVER_EXPORT_KEY: 'fax_recipientTo',
  RECEIVED_FROM_EXPORT_KEY: 'sender_name',
  DATE_AND_TIME_KEY: 'created_at',
  DATE_AND_TIME_KEY_CAMELCASE: 'createdAt',
  DATE_AND_TIME_KEY_UPPERCASE: 'CREATED_AT',
  COMMUNICATION_ID_KEY_UPPERCASE: 'COMMUNICATION_ID',
  COMMUNICATION_STATUS_UPPERCASE: 'COMMUNICATION_STATUS',
  LAST_UPDATED: 'Last Updated By',
  LAST_UPDATED_KEY: 'modified_by',
  MODE: 'Mode',
  CHANNEL: 'Channel',
  MODE_KEY: 'category_type_id',
  MODE_KEY_UPPERCASE: 'COMMUNICATION_TYPE',
  MODE_KEY_LOWERCASE: 'communication_type',
  STATUS: 'Status',
  REFERENCE_ID: 'Reference ID',
  REFERENCE_ID_KEY: 'message_id',
  FAX_STATUS: 'Fax Status',
  STATUS_KEY: 'category_status_id',
  FAX_STATUS_KEY: 'faxStatus',
  STATUS_KEY_UPPERCASE: 'STATUS',
  OUTBOUND_LOWERCASE: 'outbound',
  OUTBOUND: 'Outbound',
  OUTBOUND_UPPERCASE: 'OUTBOUND',
  INBOUND: 'Inbound',
  INBOUND_WEB: 'Inbound Web',
  INBOUND_WEB_KEY: 'inbound_web',
  INBOUND_FAX: 'Inbound Fax',
  INBOUND_FAX_LOWERCASE: 'inbound_fax',
  INBOUND_SMS: 'Inbound SMS',
  INBOUND_SMS_LOWERCASE: 'inbound_sms',
  INBOUND_LOWERCASE: 'inbound',
  NO_CORRESPONDENCE_DATA: 'No Correspondence Logs',
  COMMUNICATION_STATUS_KEY: 'COMMUNICATION_STATUS',
  COMPONENT: 'component',
  BETWEEN: 'BETWEEN',
  EQUALS: 'EQUALS',
  LIKE: 'LIKE',
  DAYS: 'days',
  DISABLED: 'disabled',
  DESC: 'desc',
  ASC: 'asc',
  EMAIL_COPY_SUCCESS_MSG: 'Content copied to clipboard.',
  EMAIL_COPY_ERROR_MSG: 'Failed to copy content.',
  RECORD_STATUS_KEY: 'RECORD_STATUS',
  ACTIVE: 'ACTIVE',
  CASE_TYPE_UPPERCASE: 'CASE_TYPE',
  INBOUND_MAIL: 'Inbound Mail',
  INBOUND_MAIL_LOWERCASE: 'inbound_mail',
}

export const searchValues = {
  INBOUND_SMS: 'S-',
  INBOUND_FAX: 'F-',
  INBOUND_MAIL: 'M-',
}

export const emailTemplate = {
  Insert_Dynamic_Fields: ' Insert Dynamic Fields',
  View_Email_Template: ' View Email Template',
  Edit_Email_Template: ' Edit Email Template',
  Email_Template: 'Email Template',
  Configure_Email_Template: 'Configure Email Template',
  View_LETTER_Template: ' View Letter Template',
  Edit_LETTER_Template: ' Edit Letter Template',
  LETTER_Template: 'Letter Template',
  Configure_LETTER_Template: 'Configure Letter Template',
  CANCEL: 'Cancel',
  EDIT: 'Edit',
  SAVE: 'Save',
  Save_as_Draft: 'Save as Draft',
  Template_Name: 'Template Name',
  To: 'To',
  Cc: 'Cc',
  Bcc: 'Bcc',
  Subject: 'Subject',
  Body: 'Body',
  Static_Documents: 'Static Documents',
  Dynamic_Documents: 'Dynamic Documents',
  Include_Editable_Texts: 'Include Editable Texts',
  Recipients: 'Recipients',
  Sheet_Cover: 'Cover Sheet',
  TEMPLATE_MANAGEMENT: 'Communication Templates',
  bulkDisableButton: 'Bulk Disable',
  bulkEnableButton: 'Bulk Enable',
  STATIC_DOCUMENT: 'STATIC_DOCUMENT',
  DYNAMIC_DOCUMENT: 'DYNAMIC_DOCUMENT',
  COVER_LETTER: 'COVER_LETTER',
  LETTER: 'LETTER',
  EMAIL: 'EMAIL',
  FAX: 'FAX',
  SMS: 'SMS',
  WEB: 'WEB',
  WEB_ENROLLMENT_PREVIEW: 'Web Enrollment Preview',
  Email: 'Email',
  Email_Preview: 'Email Preview',
  Letter: 'Letter',
  Letter_Preview: 'Letter Preview',
  Fax: 'Fax',
  Fax_Preview: 'Fax Preview',
  Sms: 'Sms',
  Sms_Preview: 'Sms Preview',
  DRAFT: 'Draft',
  DOCUMENT_PREVIEW: 'Document Preview',
  PHONE: 'Phone',
  POSTALMAIL: 'POSTALMAIL',
  Mail: 'Mail',
  MAIL_PREVIEW: 'Mail Preview',
}

export const FIELD_VALUE_CONSTANTS = {
  ZERO: 0,
  ONE: 1,
  TWO: 2,
  THREE: 3,
  FIVE: 5,
  SEVEN: '7',
  TEN: 10,
  SIXTEEN: 16,
  TWENTY: 20,
  TWENTY_FIVE: 25,
  FIFTY: 50,
  FIFTY_NINE: 59,
  HUNDRED: 100,
  ONE_HUNDRED_TWENTY: 120,
  ONE_HUNDRED_SIXTY: 160,
  TWO_HUNDRED: 200,
  TWO_HUNDRED_FIFTY_FIVE: 255,
  FIVE_HUNDRED: 500,
  EIGHT_FOUR_TWO: 842,
  TWO_THOUSAND: 2000,
  TWO_HUNDRED_FIFTY: 250,
  TWO_THOUSAND_FORTY_EIGHT: 2048,
  FOUR_HUNDRED: 400,
  SIX_HUNDRED: 600,
  MAXIMUM_ALLOWED_NUMBER: 2147483647,
  INVALID: '-1',
}

export const COMM_TEMPLATE = {
  INSERT_PLACEHOLDERS: 'Insert Placeholders',
  VIEW_FAX_TEMPLATE: 'View Fax Template',
  EDIT_FAX_TEMPLATE: 'Edit Fax Template',
  FAX_TEMPLATE: 'Fax Template',
  CONFIGURE_FAX_TEMPLATE: 'Configure Fax Template',
  INCLUDE_EDITABLE_TEXTS: 'Include Editable Texts',
  DYNAMIC_DOCUMENTS: 'Dynamic Documents',
  STATIC_DOCUMENTS: 'Static Documents',
  COVER_SHEET: 'Cover Sheet',
  RECIPIENTS: 'Recipients',
  SERVICE: 'Service',
  PROGRAM: 'Program',
  TEMPLATE_NAME: 'Template Name',
  COMMUNICATION_TEMPLATE: 'Communication Template',
  SERVICE_UPPERCASE: 'SERVICE',
  PROGRAM_UPPERCASE: 'PROGRAM',
  TEMPLATE_NAME_UPPERCASE: 'TEMPLATE_NAME',
  CORRESPONDANCE_TYPE_UPPERCASE: 'CORRESPONDENCE_TYPE',
  RECIPIENT_PHONE_NUMBER: 'Recipient Phone Number',
  //SMS
  VIEW_SMS_TEMPLATE: 'View SMS Template',
  EDIT_SMS_TEMPLATE: 'Edit SMS Template',
  SMS_TEMPLATE: 'SMS Template',
  CONFIGURE_SMS_TEMPLATE: 'Configure SMS Template',
  EMAIL: 'Email',
  FAX: 'Fax',
  LETTER: 'Letter',
  SMS: 'SMS',
  CHECKBOX: 'checkbox',
  CORRESPONDANCE_TYPE: 'Correspondence Type',
  EMAIL_UPPERCASE: 'EMAIL',
  FAX_UPPERCASE: 'FAX',
  LETTER_UPPERCASE: 'LETTER',
  SMS_UPPERCASE: 'SMS',
  DYNAMIC_DOCUMENT_UPPERCASE: 'DYNAMIC_DOCUMENT',
  DYNAMIC_DOCUMENT: 'DYNAMIC',
  STATIC_DOCUMENT_UPPERCASE: 'STATIC_DOCUMENT',
  STATIC_DOCUMENT: 'STATIC',
  COVER_LETTER_UPPERCASE: 'COVER_LETTER',
  EMAIL_LOWECASE: 'email',
  FAX_LOWECASE: 'fax',
  SMS_LOWECASE: 'sms',
  LETTER_EN: 'Letter',
  LETTER_LOWECASE: 'letter',
  MESSAGE_TEXT: 'Message Text',

  // Send Manual Communication
  SEND_MAIL_COMMUNICATION: 'Send Manual Communication',
  SEND_MAIL_COMMUNICATION_MESSAGE: 'Send Email,SMS,Fax or Letter for the case',
  CASE_ID: 'Case ID',
  MODE_OF_COMM: 'Mode of Communication',
  GENERATE: 'Generate',
  GENERATE_PREVIEW: 'Generate Preview',
  TO: 'To',
  CC: 'Cc',
  BCC: 'Bcc',
  SUBJECT: 'Subject',
  ATTACHMENTS: 'Attachments',
  PREVIEW: 'Preview',
  REVIEW_EDITABLE_TEXT: 'Review Editable Text',
  EDIT_DOCUMENT: 'Edit Document',
  VIEW_DOCUMENT: 'View Document',
  SMS_RECIPIENTS: 'SMS Recipients',
  FAX_RECIPIENTS: 'FAX Recipients',
  RECIPIENTS_FAX_NUMBER: 'Recipient Fax Number',
  RECIPIENTS_SMS_NUMBER: 'Recipient SMS Number',
  ADDRESS: 'Address',
  RECIPIENT_MAILING_ADDRESS: 'Recipient Mailing Address',
  COVER_SHEET_KEY: 'COVER_LETTER', //might need to be changed later
  SUCCESSFULLY_QUEUED: ' Successfully queued for sending',
  FAILED_TO_QUEUE: 'Failed to queue ',
  RECIPIENT_FAX_NUMBER: 'Recipient Fax Number',
  RECIPIENT_LETTER_ADDRESS: 'Recipient Letter Address',
  UPLOAD: 'Upload',
  NO_ATTACHED_DOCUMENTS: 'No attached Docx.',
  CASE_ATTACHMENTS: 'Case Attachments',
  FAX_ATTACHMENTS: 'Fax Attachments',
  FAX_TRANSMISSION_STATUS: 'Fax Transmission Status',
  COVERSHEET_PREVIEW: 'Coversheet Preview',
  DOWNLOADED_SUCCESSFULLY: 'Document Downloaded Successfully',
  SAVE_TO_ATTACHMENTS: 'Save to Attachments',
  CASE_DOCUMENT_TYPE: 'Case Document Type',
  OTHER: 'Other',
  RECIPIENT_NAME: 'Recipient Name',

  //Keys
  STATIC_DOCUMENTS_KEY: 'static_documents',
  DYNAMIC_DOCUMENTS_KEY: 'dynamic_documents',
  CLIENT_DROPDOWN_KEY: 'Client',
  GLOBAL: 'Global',
  PDF_EXTENSION: '.pdf',
  _COPY: '_copy',
  COPY: 'Copy',
  COPY_LOWERCASE: 'copy',
  DRAFT: '(Draft)',
  TRUE: 'true',

  //Preview While Creation
  EMAIL_PREVIEW: 'Email Preview',
  SMS_PREVIEW: 'SMS Preview',
  FAX_PREVIEW: 'FAX Preview',
  LETTER_PREVIEW: 'Letter Preview',
  PREVIEW_DYNAMIC_DOCUMENT: 'preview_dynamic_document',
  PREVIEW_STATIC_DOCUMENT: 'preview_static_document',
  DOCUMENT_PREVIEW: 'Document Preview',
  DOCUMENT_NAME: 'Document Name',
  FILE_PREVIEW: 'File Preview',

  ERROR_FETCHING_DATA: 'Error fetching data',
  SMS_MESSAGE: 'SMS Message',
  EMAIL_CAPITAL: 'EMAIL',
  SMS_CAPITAL: 'SMS',
  FAX_CAPITAL: 'FAX',
  LETTER_CAPITAL: 'LETTER',
  DRAFT_WITH_BRACES: ' (Draft)',
  CONFLICT_RECIPIENT_TOASTER_MESSAGE:
    'Recipient Email IDs in To,Cc & Bcc can not be same',
  CONFLICT_RECIPIENT_API_MESSAGE: 'Duplicate in Recipients',
  DRAG_TO_CHANGE_MESSAGE: 'Drag to Change the Order of documents.',
  ERR_UPLOADING_FILE: 'Error uploading file',
  FILE_UPLOADED_SUCCESSFULLY: 'File uploaded successfully!',
  ERROR_FETCHING: 'Error fetching ',
  COVERSHEET: 'Coversheet',
  ENTITY: ' Entity',
  DOCUMENT_TEMPLATE: 'Document Template',
  DOCX_UPPERCASE: 'DOCX',
  ERROR_FETCHING_DOCUMENT: 'Error fetching document',
  PLEASE_ENTER_A_VALID_RECIPIENT: 'Please enter a valid recipient',
  PHONE_NO: 'Phone No.',
  ATTACHMENT_VALIDATION_WHILE_SENDING_MSG:
    'Please attach document in Fax Attachments to send a fax',
  ATTACHMENT_VALIDATION_WHILE_SENDING_MSG_EMAIL:
    'Please attach document in Email Attachments to send a email',
  ATTACHMENT_VALIDATION_WHILE_SENDING_MSG_LETTER:
    'Please attach document in Letter Attachments to send a letter',
  INSURANCE_LABEL: 'Insurance',
  OUTBOUND_MAIL_PATIENT_ONLY_MESSAGE: 'Outbound mail allowed for patient only.',
  PRIMARY: '(Primary)',
}

export const PERMISSION_ASSIGNMENT = {
  ASSIGN_PERMISSIONS: 'Assign Permissions',
  SELECT_PERMISSIONS: 'Select Permissions',
  ASSIGN_PERMISSIONS_SUBHEADING:
    'Please select a User ID from the dropdown to assign the appropriate roles.',
  SELECT_PROGRAMS_HEADING: 'Select Program & Assign Permissions Set',
  SELECT_GLOBAL_HEADING: 'Assign Global Permissions Set',
  GLOBAL_PERMISSION_HEADING: 'Assign Permissions Set',
  GLOBAL_PERMISSION_MESSAGE:
    'Global Permissions have been saved for selected user!',
  SELECT_PROGRAMS_SUBHEADING:
    'Please select program from the dropdown list and assign permission sets',
  PROGRAM: 'Program',
  ADD_BUTTON: 'Add',
  PROGRAM_REQUIRED_MESSAGE: 'Program must be selected',
  PERMISSION_SET_REQUIRED_MESSAGE:
    'At least one permission set must be assigned',
  VIEW_ASSIGNED_PERMISSIONS: 'View Assigned Permissions',
  ASSIGN_PERMISSION_SETS: 'Assign Permission Sets',
  SAVE_CONFIRMATION_MESSAGE:
    'Are you sure you want to assign these permission sets?',
  USER_SELECT_MESSAGE: 'At least one user must be selected',
  ASSIGN_PERMISSION_MESSAGE:
    'At least one entry must be there in the table below',
  SAVE_SUCCESS_MESSAGE: 'Permission sets assigned successfully.',
  RECORD_CONFLICT_MESSAGE:
    'Remove these users since permissions for specified programs are already assigned.',
  CLEAR: 'Clear',
  CLONE_SUCCESS_MESSAGE: 'Permissions cloned successfully.',
  EDIT_SUCCESS_MESSAGE: 'Permissions updated successfully.',
  NO_ACCESS_FOR_CASE: 'You do not have access to this case.',
}
export const ATTACHMENT_FIELD_OPTIONS = {
  PREVIEW: 'Preview',
  COPY: 'Copy',
  DOWNLOAD: 'Download',
  PRINT: 'Print',
  PREVIEW_LOWERCASE: 'preview',
  DOWNLOAD_LOWERCASE: 'download',
}
export const MODE_STATUS = {
  SUCCESS: 'Success',
  ERROR: 'ERROR',
  INITIAL: 'INITIAL',
  FAILED: 'Failed',
  INCORRECT: 'incorrect',
  REQUIRED: 'required',
  IN_PROGRESS_VAL: 'In Progress',
  IN_PROGRESS_KEY: 'In_Progress',
  RECEIVED: 'Received',
  DISCARDED: 'Discarded',
  COMPLETED: 'Completed',
  PENDING: 'Pending',
  RESOLVED: 'Resolved',
  SPLIT: 'Split',
  PENDING_DISPATCH: 'Pending Dispatch',
  PENDING_DISPATCH_KEY: 'Pending_Dispatch',
  TRANSMISSION_SUCCESSFUL: 'Transmission Successful',
  TRANSMISSION_SUCCESSFUL_KEY: 'Transmission_Successful',
  TRANSMISSION_IN_PROGRESS: 'Transmission In Progress',
  TRANSMISSION_IN_PROGRESS_KEY: 'Transmission_In_Progress',
  TRANSMISSION_FAILED: 'Transmission Failed',
  TRANSMISSION_FAILED_KEY: 'Transmission_Failed',
  PROCESSING_PRINTING: 'Processing Printing',
  PROCESSING_PRINTING_KEY: 'Processing_Printing',
}

export const excludedFilterFields = [componentNames.FILE_UPLOAD] as string[]

export const DASHBOARD = {
  HOMEPAGE: 'Dashboard',
  TODAY_STATS: "Today's Stats",
  PROGRAM: 'Program',
  AGENT: 'Agent',
  SUPERVISOR: 'Supervisor',
  AGENT_DASHBOARD: 'agent',
  SUPERVISOR_DASHBOARD: 'supervisor',
  COUNT_BY_AGE: 'Count of Intake by Age',
  COUNT_APPLICATION: 'Count of Application',
  COUNT_OF_DOCUMENT: 'Count of Documents',
  BAR_LOWERCASE: 'bar',
  TOTAL_INTAKE: 'Total Intakes to be Processed',
  TOTAL_CASES_TO_BE_PROCESSED: 'Total Cases (Excluding MI)',
  DOCUMENTS_REVIEWED: 'Total Documents to be Reviewed',
  UNASSIGNED: 'Unassigned',
  DOCUMENT: 'Document',
  INTAKE: 'Intake',
  AE_PC_KEY: 'ae-pc',
  UNASSIGNED_LOWERCASE: 'unassigned',
  OVERDUE: 'Overdue',
  AT_RISK: 'At Risk',
  ON_TRACK: 'On Track',
  PAUSED_MI: 'Paused(MI)',
  AGE_BUCKET: 'AGE_BUCKET',
  INTAKE_TILES_SUMMARY: 'INTAKE_TILES_SUMMARY',
  CASE_TILES_SUMMARY: 'CASE_TILES_SUMMARY',
  AE_PC_TILES_SUMMARY: 'AE_PC_TILES_SUMMARY',
  INTAKE_USER_WISE: 'INTAKE_USER_WISE',
  ALL_CASES: 'All Cases',
  CASE_USER_WISE: 'CASE_USER_WISE',
  AE_PC_USER_WISE: 'AE_PC_USER_WISE',
  UNASSIGNED_MESSAGE:
    'Intake has no owner yet. Assign it to someone so it doesn’t become At Risk or Overdue without action.',
  OVERDUE_MESSAGE: 'Intake has passed its due date (deadline already missed).',
  AT_RISK_MESSAGE: 'Intake is close to its due date (deadline coming soon).',
  ON_TRACK_MESSAGE: 'Intake is comfortably within its deadline.',
  REPORTABLE_INCIDENT: 'Reportable Incident',
  PAP_FULL_FORM: 'Patient Assistance Program',
  CASE_DASHBOARD: 'Case Dashboard',
  AE_PC_DASHBOARD: 'AE PC Dashboard',
  RI: 'RI',
  DOCUMENTS_DATA: 'Documents Data',
  CASES: 'Cases',
  INTAKE_DATA: 'Intake Data',
  MY_CASES: 'My Cases',
  MY_TASKS: 'My Tasks',
  OVERDUE_CASE_MESSAGE:
    'Case has passed its due date (deadline already missed). Determined when current working time is later than its calculated due date (weekends/holidays are skipped in the calculation).',
  AT_RISK_CASE_MESSAGE: 'Case is close to its due date (deadline coming soon).',
  ON_TRACK_CASE_MESSAGE: 'Case is comfortably within its deadline.',
  TOTAL_CASE_MESSAGE:
    'Total no. of Cases that are being processed or yet to be processed.',
  PAUSED_CASE_MESSAGE: 'Cases that are in Missing Information',
  UNASSIGNED_CASE_MESSAGE:
    'Case has no owner yet. Assign it to someone so it doesn’t become At Risk or Overdue without action.',
  OVERDUE_DOCUMENT_MESSAGE: 'Overdue >20 Hrs',
  AT_RISK_DOCUMENT_MESSAGE: 'At Risk >16 Hrs',
  ON_TRACK_DOCUMENT_MESSAGE: 'Document is comfortably within its deadline.',
  TOTAL_DOCUMENT_MESSAGE:
    'Total no. of Documents that are being reviewed or yet to be reviewed.',
  UNASSIGNED_DOCUMENT_MESSAGE:
    'Document has no owner yet. Assign it to someone so it doesn’t become At Risk or Overdue without action.',
  SHOW_DASHBOARD: 'Show Dashboard',
  SHOW_AE_PC_DASHBOARD: 'Show AE PC Dashboard',
  WEB_INTAKE_DASHBOARD: 'Web Intake Dashboard',
  FAX_INTAKE_DASHBOARD: 'Fax Intake Dashboard',
}
export const PERMISSION_MODULES = {
  CORRESPONDENCE_LOGS: 'Correspondence Logs',
  PERMISSION_ASSIGNMENT: 'Permission Set Assignment',
  WORK_ASSIGNMENT: 'Work Assignment',
  WORK_REASSIGNMENT: 'Work Reassignment',
  BULK_WORK_ASSIGNMENTS: 'Bulk Work Assignments',
  INTAKE_TRIAGE: 'Intake Triage',
  MANAGE_ATTACHMENTS: 'Manage Attachments',
  QUEUE_AND_CASE_VISIBILITY: 'Queue and Case Visibility',
  PRINT_MAIL: 'Print Mail',
  MANAGE_CLIENT_CONTROLLED_DATA: 'Manage Client Controlled Data',
  MANAGE_SENSITIVE_DATA: 'Manage Sensitive Data',
  AGENT_DASHBOARD: 'Agent Dashboard',
  SUPERVISOR_DASHBOARD: 'Supervisor Dashboard',
  MANAGE_GLOBAL_DATA: 'Manage Global Data',
  OVERRIDE_AE_PC: 'Override AE/PC',
  DISCARD_INTAKES: 'Discard Intakes',
}

export enum PERMISSION_ACCESSIBILITY {
  VIEW = 'View Only',
  MANAGE = 'Manage',
}

export const ENTITY_ID = {
  COMMUNICATION: 59,
  PROGRAM: 94,
  PRODUCT: 78,
  PATIENT: 18,
  PROVIDER: 4,
  REQUESTOR: 1161,
  USER: 74,
  CASE_SUMMARY: 1311,
  CASE: 32,
  HCP_PORTAL_ENTITY: 90,
  FACILITY: 5,
  PATIENT_ADDRESS: 1302,
}

export const CATEGORY_VALUES_COMMUNICATION_TYPE = {
  PHONE: '1133',
}

export const ADVANCE_SEARCH_ENTITY_NAMES_MAPPING = {
  PROVIDER: 'Provider',
  FACILITY: 'Facility',
}

export const REPORTS = {
  FILTERS: 'Filters:',
  RESET_FILTERS: 'Reset Filters',
  REPORTS: 'Reports',
  INTAKE_VS_CLOSURE_LABEL: 'Intake vs Closure',
  INTAKE_VS_CLOSURE_KEY: 'INTAKEVSCLOSURE',
  TURN_AROUND_TIME_LABEL: 'Turn Around Time',
  SLA_COMPLETION: 'SLA Comp.',
  AGENT_PRODUCTIVITY_LABEL: 'Agent Productivity',
  INTAKE: 'Intake',
  CLOSURE: 'Closure',
  VOLUME: 'Volume',
  ON_TRACK: 'On Track',
  AT_RISK: 'At Risk',
  OVERDUE: 'Overdue',
  PAUSED: 'Paused',
  TAT_KEY: 'TAT',
  AGENT_PRODUCTIVITY_KEY: 'AGENTPRODUCTIVITY',
  SLA_KEY: 'SLA',
  WEEKLY: 'Weekly',
  SERVICES: 'Services',
  AGENTS: 'Agents',
  THIS_WEEK: 'This Week',
  LAST_WEEK: 'Last Week',
  CASE_TYPE: 'Case Type',
  INTAKE_TYPE: 'Intake Type',
  INTAKE_VS_CLOSURE_API_FAIL: 'Error Fetching Intake Vs Closure data',
  ERROR_FETCHING_SLA_DATA: 'Error fetching SLA data',
  RESET_FILTERS_INTAKE_CLOSURE: 'reset_filters_intake_closure',
  RESET_FILTERS_SLA_COMP: 'reset_filters_sla_comp',
  RESET_FILTERS_TAT: 'reset_filters_tat',
  RESET_FILTERS_AGENT_PRODCUTIVITY: 'reset_filters_agent_productivity',
  ON_TRACK_KEY: 'ON_TRACK',
  AT_RISK_KEY: 'AT_RISK',
  OVERDUE_KEY: 'OVERDUE',
  INTAKE_TYPES_ARRAY: ['fax', 'web'],
  FAX: 'Fax',
  WEB: 'Web',
  FAX_KEY: 'fax',
  WEB_KEY: 'web',
  TAT_IN_MINS: 'TAT (in mins)',
  CREATE_CASE: 'Create Case',
  CREATE_CASE_KEY: 'create_case',
}

export const USER_PROFILES = {
  STANDARD_USER: 'Standard User',
  PROGRAM_ADMIN: 'Program Admin',
  SUPERADMIN: 'System Admin',
  READ_ONLY_USER: 'Read-Only User',
  SUPPORT: 'Support',
  SUPPORT_USER: 'Support',
}

export const WORKFLOW_STATUS = {
  COMPLETED: 'COMPLETED',
  PENDING: 'PENDING',
  INPROGRESS: 'INPROGRESS',
  OPEN: 'OPEN',
  CANCELLED: 'CANCELLED',
  DATE_ERROR_MSG: 'Start date must be before or equal to due date',
  DISCARDED: 'DISCARDED',
  PROCESSING_IN_BACKGROUND: 'PROCESSING_IN_BACKGROUND',
}

export const INTERACTION_STATUS = {
  COMPLETED: 'Completed',
  OPEN: 'Open',
  CANCELLED: 'Cancelled',
  DISCARDED: 'Discarded',
  INVALID_VOICEMAIL: 'Invalid Voicemail',
  RE_OPEN: 'Re-open',
}

export const INTERACTION_STATUS_KEYS = {
  DISCARDED: 'DISCARDED',
}

export const AWS_CONNECT_STATE = {
  INCOMING: 'incoming',
  CONNECTING: 'connecting',
  OUTGOING: 'outgoing',
  ACW: 'ACW',
  ENDED: 'ended',
  CONNECTED: 'connected',
  DESTROYED: 'destroyed',
  MISSED: 'missed',
}

export const SLA_COLORS = {
  ON_TRACK: '#3C9A5F',
  AT_RISK: '#F9B934',
  OVERDUE: '#D94141',
}

export const REPORT_COLORS = {
  GREEN: '#28B463',
}

export const PAGINATION = {
  ROWS_PER_PAGE: 25,
}

export enum DAY {
  Sun = 'Sun',
  Mon = 'Mon',
  Tue = 'Tue',
  Wed = 'Wed',
  Thu = 'Thu',
  Fri = 'Fri',
  Sat = 'Sat',
}

export enum REPORT_DAYS {
  Mon = 'Mon',
  Tue = 'Tue',
  Wed = 'Wed',
  Thu = 'Thu',
  Fri = 'Fri',
  Sat = 'Sat',
  Sun = 'Sun',
}
export const TABLE_HEADER_KEYS_ARRAY = [
  'modified_at',
  'users',
  'user_id',
  'case_primary_id',
]

export const EMPTY_TABLE_DATA_MESSAGE = {
  NO_CASE_ASSIGNED: 'No case is assigned to you.',
  NO_TASK_ASSIGNED: 'No task is assigned to you.',
  NO_FAXES_AVAILABLE: 'No faxes available for the selected client.',
  NO_MAILS_AVAILABLE: 'No mails available for the selected client.',
  NO_WEB_AVAILABLE: 'No web available for the selected client.',
  NO_RECORDS_FOUND: 'No records found',
  NO_LETTERS_AVAILABLE: 'No letters available for the selected client.',
}

export const TASK_STATUS = {
  PENDING: 'PENDING',
  ACTIVE: 'ACTIVE',
}

export const MANUAL_TASK_CONSTANTS = {
  PENDING: { value: 'PENDING', label: 'Pending' },
  COMPLETED: { value: 'COMPLETED', label: 'Completed' },
  OPEN: { value: 'OPEN', label: 'Open' },
}

export const TASK_TYPE_CONSTANTS = {
  FAX_OUTBOUND: 'Fax - Outbound',
  DOCUMENT_GENERATION: 'Document Generation',
  CALL_OUTBOUND: 'Call - Outbound',
  TO_DO: 'To Do',
  TEXT_OUTBOUND: 'Text - Outbound',
  MAIL_OUTBOUND: 'Mail - Outbound',
}

export const PATIENT_DETAILS = [
  'first_name',
  'last_name',
  'date_of_birth',
  'gender',
  'email',
  'c_patient_phones_c_patients',
]

export const ENTITY_NAMES_RELATION_MAPPING = {
  PATIENT: 'c_patients',
  FACILITY: 'c_facilities',
}

export const EXTENSIONS = {
  PDF: 'pdf',
  DOCX: 'docx',
  XLSX: 'xlsx',
  XLS: 'xls',
  DOC: 'doc',
  DOCM: 'docm',
  XLSM: 'xlsm',
  TXT: 'txt',
  CSV: 'csv',
  JPG: 'jpg',
  PNG: 'png',
  JPEG: 'jpeg',
  IMAGE: 'image',
  UNSUPPORTED: 'unsupported',
}

export const OUTBOUND_ID_CONSTANTS = {
  FAX_OUTBOUND_ID: 9,
  MAIL_OUTBOUND_ID: 11,
  GENERATE_DOCUMENT_ID: 48,
}

export const MODAL_POPUP_ACTIONS = {
  OK_UPPERCASE: 'OK',
}

export const FIELD_NAMES = {
  CASE_DOCUMENTS: 'c_case_documents_c_cases',
  C_CASE_DOCUMENTS: 'c_case_documents',
  CREATED_AT: 'created_at',
  RECORD_ID: 'record_id',
  ENTITY_TYPE: 'entity_type',
  CATEGORY: 'category',
  C_CASE_STATUSES: 'c_case_statuses',
  OVERALL_CASE_STATUS: 'overall_case_status',
}

export const PAYSIGN_ERROR_STATUS_CODES = [401, 403, 404, 405, 408]

export const PHONE_TYPE_CATEGORY_VALUES = {
  FAX: 112,
  PHONE: 111,
}

export const WORKFLOW_ACTION_IDENTIFIER = {
  SELECT_SINGLE_PAYER: 'SELECT_SINGLE_PAYER',
}

export const MISSING_INFO_POPUP_CONSTANTS = {
  MISSING_INFORMATION_SUMMARY: 'Missing Information Summary',
  MI_FIELD: 'MI Field',
  MI_FIELD_LOWERCASE: 'mi_field',
  MI_FIELD_LABEL: 'field_label',
  MI_START_DATE_LOWERCASE: 'mi_start_date',
  MI_END_DATE_LOWERCASE: 'mi_end_date',
  RESOLVED_ON: 'resolved_on',
  PENDING_MI_FIELDS: 'Pending MI Fields',
  RESOLVED_MI_FIELDS: 'Resolved MI Fields',
  OVERALL_MI_CASE_DETAILS: 'Overall MI Case Details',
  MI_END_DATE: 'MI End Date',
  MI_START_DATE: 'MI Start Date',
  MI_TIME_TOTAL: 'MI Time Total',
  ADD_MISSING_INFO: 'Add Missing Information',
  EDIT_MISSING_INFO: 'Edit Missing Information',
  RE_VALIDATE: 'Re-validate',
  COMPONENT: 'component',
  MISSING_INFORMATION_INTERRUPT: 'missingInformation',
  SAVE_UPPERCASE: 'SAVE',
  REVALIDATE_UPPERCASE: 'REVALIDATE',
  ICON_CLICK: 'ICON_CLICK',
  INITIAL_LOAD: 'INITIAL_LOAD',
  PENDING_MI_DATA: 'pendingMiData',
  CLOSED: 'closed',
  MANUAL: 'Manual',
  SYSTEM: 'System',
  MI_FIELD_TYPE: 'mi_field_type',
  MI_FIELD_TYPE_LABEL: 'MI Field Type',
  UNSAVED: 'Unsaved',
}
export const HYBRID_LAYOUT_CONSTANTS = {
  NESTING_DATA_KEY: '__singlelookup__data',
  ERROR_KEY: '__ERROR_KEY__',
  EXTRA_FIELDS: '__extraKey__',
  SUB_TOTAL_KEY: 'subTotal',
  UNWATCH_FIELD_KEY: '__UNWATCH_FIELD__',
}
export const PATIENT_LAYOUT_KEYWORDS = {
  FIRST_NAME: 'first_name',
  LAST_NAME: 'last_name',
  DATE_OF_BIRTH: 'date_of_birth',
}
export const HYBRID_LAYOUT_ROW_TYPE = {
  BASE_LAYOUT: 'base_layout',
  ASSOCIATED_SINGLELOOKUP_LAYOUT: 'associated_singlelookup_layout',
  INSURANCE_INFORMATION_COMPONENT: 'insurance_information_component',
  INSURANCE_INFORMATION_COMPONENT_NOVO: 'insurance_information_component_novo',
}

export const HYBRID_LAYOUT_COUSTOM_IN_ROW_COMPONENTS_CONSTANTS = {
  APPLICATION_VERSION: 'APPLICATION_VERSION',
  EXCEPTION_ICD_CODES: 'EXCEPTION_ICD_CODES',
}

export const STATUS_IDS = {
  FAX_RECEIVED: 68,
  WEB_RECEIVED: 1102,
}

interface StatusStyle {
  label: string
  bgColor: string
  textColor: string
}

export const StatusConfig: Record<string, StatusStyle> = {
  SUCCESS: {
    label: 'SUCCESS',
    bgColor: 'bg-green-100',
    textColor: 'text-accent-900',
  },
  SUCCESSFUL: {
    label: 'SUCCESSFUL',
    bgColor: 'bg-green-100',
    textColor: 'text-accent-900',
  },
  COMPLETED: {
    label: 'COMPLETED',
    bgColor: 'bg-green-100',
    textColor: 'text-accent-900',
  },
  SPLIT: {
    label: 'SPLIT',
    bgColor: 'bg-success-100',
    textColor: 'text-accent-900',
  },
  IN_PROGRESS: {
    label: 'IN PROGRESS',
    bgColor: 'bg-progress-100',
    textColor: 'text-accent-900',
  },
  RECEIVED: {
    label: 'RECEIVED',
    bgColor: 'bg-progress-100',
    textColor: 'text-accent-900',
  },
  FAILED: {
    label: 'FAILED',
    bgColor: 'bg-red-100',
    textColor: 'text-accent-900',
  },
  PENDING_DISPATCH: {
    label: 'PENDING DISPATCH',
    bgColor: 'bg-yellow-50',
    textColor: 'text-accent-900',
  },
  TRANSMISSION_SUCCESSFUL: {
    label: 'TRANSMISSION SUCCESSFUL',
    bgColor: 'bg-success-100',
    textColor: 'text-accent-900',
  },
  TRANSMISSION_FAILED: {
    label: 'TRANSMISSION FAILED',
    bgColor: 'bg-red-50',
    textColor: 'text-accent-900',
  },
  TRANSMISSION_IN_PROGRESS: {
    label: 'TRANSMISSION IN PROGRESS',
    bgColor: 'bg-yellow-50',
    textColor: 'text-accent-900',
  },
  PROCESSING_PRINTING: {
    label: 'PROCESSING PRINTING',
    bgColor: 'bg-secondary-100',
    textColor: 'text-accent-900',
  },
  DISCARDED: {
    label: 'DISCARDED',
    bgColor: 'bg-red-50',
    textColor: 'text-accent-900',
  },
  PROCESSING: {
    label: 'PROCESSING',
    bgColor: 'bg-secondary-50',
    textColor: 'text-accent-900',
  },
  DRAFT: {
    label: 'DRAFT',
    bgColor: 'bg-secondary-50',
    textColor: 'text-accent-900',
  },
  VOID: {
    label: 'VOID',
    bgColor: 'bg-red-50',
    textColor: 'text-accent-900',
  },
  PROCESSED: {
    label: 'QUEUED',
    bgColor: 'bg-progress-100',
    textColor: 'text-accent-900',
  },
  BOUNCED: {
    label: 'FAILED',
    bgColor: 'bg-red-100',
    textColor: 'text-accent-900',
  },
  DEFERRED: {
    label: 'RETRYING',
    bgColor: 'bg-progress-100',
    textColor: 'text-accent-900',
  },
  DELIVERED: {
    label: 'SUCCESSFUL',
    bgColor: 'bg-green-100',
    textColor: 'text-accent-900',
  },
  DROPPED: {
    label: 'FAILED',
    bgColor: 'bg-red-100',
    textColor: 'text-accent-900',
  },
}

export const RECORD_STATE = {
  CHANGED: 'CHANGED',
  ADDED: 'ADDED',
  REMOVED: 'REMOVED',
}

export const CONTEXT_TYPE = {
  QUEUE: 'QUEUE',
  ENTITY: 'ENTITY',
}
export const PHARMACY_TRIAGE_CONSTANTS = {
  QUEUE_DATE: 'queue_date',
  NEW_QUEUE_DATE: 'new_queue_date',
  QUEUE_DATE_LABEL: 'Queued Date',
  NEW_QUEUE_DATE_LABEL: 'New Queued Date',

  ELIGIBILITY_CRITERIA: 'criteria',
  ELIGIBILITY_CRITERIA_LABEL: 'Eligibility Criteria',

  INPUT: 'input',
  INPUT_LABEL: 'Input',

  OUTCOME: 'outcome',
  OUTCOME_LABEL: 'Outcome',

  OUTCOME_REASON: 'outcome_reason',
  OUTCOME_REASON_LABEL: 'Outcome Reason',
}

export const PAP_WORKFLOW_CONSTANTS = {
  // Decision related
  DECISION_KEY: 'decision',
  DECISION_LABEL: 'Decision',

  DECISION_REASON_KEY: 'decision_reason',
  DECISION_REASON_LABEL: 'Decision Reason',

  EXCEPTION_REASON_KEY: 'exception_reason',
  EXCEPTION_REASON_LABEL: 'Exception Reason',

  DENIAL_REASON_KEY: 'denial_reason',
  DENIAL_REASON_LABEL: 'Denial Reason',

  REQUEST_FOR_EXCEPTION_KEY: 'request_for_exception',
  REQUEST_FOR_EXCEPTION_LABEL: 'Request for Exception',

  REQUEST_FOR_EXCEPTION_REASON_KEY: 'request_for_exception_reason',
  REQUEST_FOR_EXCEPTION_REASON_LABEL: 'Exception Request Summary',

  // Possible decision values
  DENIED: 'Denied',
  NOT_ELIGIBLE: 'Not Eligible',
  UNDETERMINED: 'Undetermined',
  ELIGIBLE: 'Eligible',

  // Enrollment fields
  ENROLLMENT_TYPE_KEY: 'enrollment_type',
  ENROLLMENT_TYPE_LABEL: 'Enrollment Type',

  ENROLLMENT_SUBTYPE_KEY: 'enrollment_subtype',
  ENROLLMENT_SUBTYPE_LABEL: 'Enrollment Subtype',
  HIDE_EXCEPTION_CHECKBOX: 'hide_exception_checkbox',
  SHOW_MAXRTE_ICON: 'show_maxrte_icon',
} as const

export const DUPLICATE_POPUP = {
  LINK: 'Link',
  LINK_KEY: 'link',
  ATTACH_KEY: 'attach',
  INTAKE_FORM: 'Intake Form',
  PROCEED_ANYWAY: 'Proceed Anyway',
  RESULT: 'Result',
  FOUND: 'Found',
  DUPLICATES_FOUND: 'Duplicates Found',
  SELECTING: 'Selecting',
  LINK_SUBTITLE_MSG:
    'associates Interaction to the duplicate case and automatically voids the newly created case.',
  ATTACH_SUBTITLE_MSG:
    'associates Intake to the duplicate case and automatically voids the newly created case.',
}
export const CUSTOM_SEPERATOR = {
  SAFE_DELIMITER: '[#SEPARATOR#]',
  ADVANCE_SEARCH_SEPERATOR: '[###ADVANCE_SEARCH_SEPARATOR###]',
} as const

export const INTEGRATION_TYPES = {
  MAXRTE: 'maxrte',
} as const
export const SIGNED_URL_REFETCH_TIMEOUT = 120000
export const MAXRTE_AUTO_SCROLL_TIMEOUT = 1000
export const AUTO_SCROLL_OFFSET_PX = 30

export const USER_PROFILE_CATEGORY = {
  SYSTEM_ADMIN: 57,
  PROGRAM_ADMIN: 58,
}

export const ACCESSOR_EXPORT_MAP: Record<string, string> = {
  communication_id: 'enrollment_id',
  case_id: 'c_case',
  case_type: 'c_case',
  program: 'hcp_program',
  product: 'product',
  received_from: 'received_from',
  received_at: 'created_at',
  agent_name: 'users',
  status: 'enrollment_status',
  patient_first_name: 'patient_first_name',
  patient_last_name: 'patient_last_name',
  quick_denial_reason: 'quick_denial_reason',
  prescriber_first_name: 'prescriber_first_name',
  prescriber_last_name: 'prescriber_last_name',
  facility_name_new: 'facility_name_new',
  requestor_type: 'requestor_type',
}

export const communicationKeyMap: Record<string, string> = {
  COMMUNICATION_ID: 'communication_id',
  CASE_ID: 'cases.case_key',
  CASE_KEY: 'cases.case_key',
  PROGRAM: 'programs.operational_program_name',
  PRODUCT: 'products.trademarked_name',
  SENDER_NAME: 'sender_name',
  AGENT_NAME: 'users.first_name',
  STATUS: 'category_status.label',
  CATEGORY_STATUS_ID: 'category_status.label',
  RECEIVED_FROM_NEW: 'sender_name',
  RECEIVED_AT: 'created_at',
  CATEGORY_TYPE_ID: 'type',
  COMMUNICATION_TYPE: 'type',
  RECIPIENT_TYPE: 'view_communication_recipient.category',
  RECIPIENT: 'view_communication_recipient.record_key',
  RECEIVER: 'view_object',
  COMMUNICATION_STATUS: 'communication_status',
}

export const communicationSortKeyMap: Record<string, string> = {
  communication_id: 'communication_id',
  category_communication_type_id: 'type',
  program: 'programs.operational_program_name',
  product: 'products.trademarked_name',
  sender_name: 'sender_name',
  agent_name: 'users.first_name',
  created_at: 'created_at',
  status: 'category_status.label',
}

export const COMMUNICATION_UI_MAPPINGS = {
  communication_id: { alias: ['Communication ID'] },
  case_key: { alias: ['Case ID'] },
  type: { alias: ['Channel'] },
  sender_name: { alias: ['Sender'] },
  service: { alias: ['Case Type'] },
  fax_recipientto: { alias: ['Sent To Fax'] },
  recipients_to: { alias: ['Sent To'] },
  created_at: { alias: ['Sent At'] },
  agent_name: { alias: ['Agent Name'] },
  message_id: { alias: ['Reference ID'] },
  status: { alias: ['Status'] },
}

export const COMMUNICATION_UI_MAPPINGS_INBOUND_FAX = {
  communication_id: { alias: ['Communication ID'] },
  case_key: { alias: ['Case ID'] },
  type: { alias: ['Channel'] },
  sender_name: { alias: ['Received From'] },
  fax_recipientto: { alias: ['Sent To'] },
  created_at: { alias: ['Received At'] },
  agent_name: { alias: ['Agent Name'] },
  message_id: { alias: ['Reference ID'] },
  status: { alias: ['Status'] },
}

export const COMMUNICATION_UI_MAPPINGS_INBOUND_SMS = {
  communication_id: { alias: ['Communication ID'] },
  case_key: { alias: ['Case ID'] },
  type: { alias: ['Channel'] },
  sender_name: { alias: ['Received From'] },
  service: { alias: ['Case Type'] },
  fax_recipientto: { alias: ['Sent To'] },
  created_at: { alias: ['Sent At'] },
  agent_name: { alias: ['Agent Name'] },
  message_id: { alias: ['Reference ID'] },
  status: { alias: ['Status'] },
  body: { alias: ['Message'] },
}

export const COMMUNICATION_ACCESSOR_KEYS = {
  COMMUNICATION_ID: 'communication_id',
  CASE_KEY: 'case_key',
  SERVICE: 'service',
  PROGRAM: 'program',
  PRODUCT: 'product',
  RECIPIENT_TYPE: 'recipient_type',
  RECIPIENT: 'recipient',
  RECEIVER: 'receiver',
  AGENT_NAME: 'agent_name',
  SENDER_NAME: 'sender_name',
  CREATED_AT: 'created_at',
  RECEIVED_AT: 'received_at',
  CATEGORY_STATUS_ID: 'category_status_id',
  STATUS: 'status',
  CATEGORY_TYPE_ID: 'category_type_id',
  CATEGORY_COMMUNICATION_TYPE_ID: 'category_communication_type_id',
}

export const COMMUNICATION_SELECT_FIELDS = {
  COMMUNICATION_ID: 'communication_id',
  CASES: 'cases',
  SERVICE: 'service',
  TYPE: 'type',
  PROGRAMS: 'programs',
  PRODUCTS: 'products',
  FAX_RECIPIENTTO: 'fax_recipientto',
  RECIPIENTS_TO: 'recipients_to',
  VIEW_COMMUNICATION_RECIPIENT: 'view_communication_recipient',
  USERS: 'users',
  SENDER_NAME: 'sender_name',
  CREATED_AT: 'created_at',
  CATEGORY_STATUS: 'category_status',
}

export const EXPORT_SELECT_FIELDS: string[] = [
  'id',
  'patient_name',
  'facility_name',
  'patient_dob',
  'facility_city',
  'hcp_program',
  'enrollment_id',
  'c_case',
  'users',
  'state',
  'received_from',
  'enrollment_status',
  'created_at',
  'product',
]

export const RELATED_ENTITIES: Record<string, RelatedEntityType> = {
  product: {
    ui_data_mappings: {
      trademarked_name: { alias: ['Product'] },
    },
    exclude_fields: ['record_id', 'entity_type', 'category'],
  },
  users: {
    ui_data_mappings: {
      first_name: { alias: ['Agent Name'] },
      last_name: { alias: ['Agent Name'] },
    },
    exclude_fields: ['record_id', 'entity_type', 'category'],
  },
  hcp_program: {
    select: ['id', 'operational_program_name'],
    ui_data_mappings: {
      operational_program_name: { alias: ['Program'] },
    },
    exclude_fields: ['record_id', 'entity_type', 'category'],
  },
  c_case: {
    ui_data_mappings: {
      case_key: { alias: ['Case Id'] },
    },
    exclude_fields: ['record_id', 'entity_type', 'category'],
  },
  services: {
    ui_data_mappings: {
      service_name: { alias: ['Case Type'] },
    },
    exclude_fields: ['record_id', 'entity_type', 'category'],
  },
  enrollment_status: {
    ui_data_mappings: {
      label: { alias: ['Status'] },
    },
    exclude_fields: ['record_id', 'entity_type', 'category'],
  },
}

export const ENTITY_TABLE_EXPORT_EVENT = 'export_entity_table'

export const EXCLUDE_FIELDS_STANDARD = ['record_id', 'entity_type', 'category']

export const EXPORT_FIELD_NAMES = {
  SERVICE: 'service',
  SERVICES: 'services',
  SERVICE_NAME: 'service_name',
  CASE_KEY: 'case_key',
  CASES: 'cases',
  RECORD_ID: 'record_id',
  ENTITY_TYPE: 'entity_type',
  CATEGORY: 'category',
}

export const ENTITY_ALIASES = {
  CASE_TYPE: 'Case Type',
}

export const EXPORT_FIELD_ALIASES = {
  c_provider: {
    provider_key: 'Provider Key',
    first_name: 'Provider First Name',
    last_name: 'Provider Last Name',
    npi: 'Provider NPI',
  },
  patient: {
    patient_key: 'Patient Key',
    first_name: 'Patient First Name',
    last_name: 'Patient Last Name',
    date_of_birth: 'Patient DOB',
  },
  users: {
    first_name: 'Assignee First Name',
    last_name: 'Assignee Last Name',
  },
  c_facilities: {
    facility_key: 'Facility Key',
    name: 'Facility Name',
  },
  c_case_statuses: {
    name: 'Case Status',
    overall_case_status: 'Overall Status',
  },
} as const

export const USER_PROFILE = {
  READ_ONLY_USER: 'Read-Only User',
}

export const INTAKE_DOCUMENT = {
  DOCUMENT_ID: 'Document ID',
  DOCUMENT_CHANNEL: 'Document Channel',
  DETERMINE_AE_PC: 'Determine AE/PC',
  OVERRIDE_STATUS: 'Override Status',
  ESCALATED: 'Escalated',
  DETERMINATION_USER_ID: 'Determination User ID',
  ACTUAL_DETERMINATION_DATE: 'Actual Determination Date',
  DETERMINATION_DUE_DATE: 'Determination Due Date',
  JUSTIFICATION_COMMENT: 'Justification Comment',
  AE_PC_INFORMATION: 'AE/PC Information',
  OVERRIDE: 'Override',
  RI_CASE_CREATION_MESSAGE: 'RI Case created successfully.',
  REPORTABLE_INCIDENT: 'Reportable Incident',
  RECEIVED_ON: 'Received On',
  DOCUMENT: 'Document',
  UNSURE: 'Unsure',
  NONE: 'None',
}

export const fieldKeyEntityMapping = {
  patient: 'PATIENT',
  c_provider: 'PROVIDER',
  c_facilities: 'FACILITY',
} as const

export const fieldKeyStagingMapping = {
  patient: 'patient',
  c_provider: 'provider',
  c_facilities: 'facility',
} as const

export const reverseFieldKeyStagingMapping = {
  patient: 'patient',
  provider: 'c_provider',
  facility: 'c_facilities',
} as const

export const LAYOUT_MODES = {
  VIEW_MODE: 'view',
  EDIT_MODE: 'edit',
  LIST_MODE: 'list',
}

export const MATCH_DEDUPE_RESULTS = {
  WEAK: 'WEAK',
  STRONG: 'STRONG',
  NO_MATCH: 'NO_MATCH',
  RECORD_CREATED: 'RECORD_CREATED',
} as const

export const MATCH_DEDUPE_RESULT_LABEL = {
  WEAK: 'Potential Matches Found',
  STRONG: 'Exact Match Found',
  NO_MATCH: 'No Match Found',
  RECORD_CREATED: 'New Record Created',
} as const

export const DEDUPE_BORDER_MAP = {
  WEAK: 'bg-primary-50 border-error-600',
  STRONG: 'bg-success-25 border-success-800',
  NO_MATCH: 'bg-primary-50 border-error-600',
  RECORD_CREATED: 'bg-success-25 border-success-800',
} as const

export const ENTITY_MAP = {
  patient: 'PATIENT',
  c_facilities: 'FACILITY',
  c_provider: 'PROVIDER',
}
export const AGE_BUCKET_CATEGORIES = [
  '00 - 04',
  '04 - 08',
  '08 - 12',
  '12 - 16',
  '16 - 20',
  '20 - 24',
  '24+',
]

export const CARD_TITLE = [
  DASHBOARD.OVERDUE,
  DASHBOARD.AT_RISK,
  DASHBOARD.ON_TRACK,
  DASHBOARD.UNASSIGNED,
  DASHBOARD.PAUSED_MI,
  DASHBOARD.TOTAL_CASES_TO_BE_PROCESSED,
]

export const GRAPH_STATUS = ['Overdue', 'At Risk', 'On Track', 'Paused(MI)']

export const FILTER_FOR_LINKED_ENTITES = ['overdue_time', 'overall_case_status']

export const FILTER_OPERATIONS = {
  LIKE: 'LIKE',
  EQUALS: 'EQUALS',
  NOT_EQUALS: 'NOT_EQUALS',
  IN: 'IN',
  BETWEEN: 'BETWEEN',
} as const

export const SORT_DIRECTIONS = {
  ASC: 'ASC',
  DESC: 'DESC',
} as const

export const BAR_HEIGHT = 28
export const CHART_PADDING = 60
export const MIN_CHART_HEIGHT = 250

export const RECIPENT_LABEL_OFFICE_FAX =
  'case.c_case_contacts_c_cases.contact.office_fax'

export const TILE_BASE_CONFIG = {
  overdue: {
    tileName: DASHBOARD.OVERDUE,
    border:
      'border-b-2 border border-b-red-600 bg-white hover:border-red-600 hover:bg-red-50',
    selected: 'border-b-2 border border-b-red-600 border-red-600 bg-red-50',
    message: DASHBOARD.OVERDUE_MESSAGE,
  },
  at_risk: {
    tileName: DASHBOARD.AT_RISK,
    border:
      'border-b-2 border border-b-yellow-600 bg-white hover:border-yellow-600 hover:bg-yellow-50',
    selected:
      'border-b-2 border border-b-yellow-600 border-yellow-600 bg-yellow-50',
    message: DASHBOARD.AT_RISK_MESSAGE,
  },
  on_track: {
    tileName: DASHBOARD.ON_TRACK,
    border:
      'border-b-2 border border-b-green-600 bg-white hover:border-green-600 hover:bg-green-50',
    selected:
      'border-b-2 border border-b-green-600 border-green-600 bg-green-50',
    message: DASHBOARD.ON_TRACK_MESSAGE,
  },
  total_intake: {
    tileName: DASHBOARD.TOTAL_INTAKE,
    border:
      'border-b-2 border border-b-blue-600 bg-white hover:border-blue-600 hover:bg-blue-50',
    selected: 'border-b-2 border border-b-blue-600 border-blue-600 bg-blue-50',
  },
  unassigned_intake: {
    tileName: DASHBOARD.UNASSIGNED,
    border:
      'border-b-2 border border-b-gray-600 bg-white hover:border-gray-600 hover:bg-gray-50',
    selected: 'border-b-2 border border-b-gray-600 border-gray-600 bg-gray-50',
    message: DASHBOARD.UNASSIGNED_MESSAGE,
  },
  overdue_case: {
    tileName: DASHBOARD.OVERDUE,
    border:
      'border-b-2 border border-b-red-600 bg-white hover:border-red-600 hover:bg-red-50',
    selected: 'border-b-2 border border-b-red-600 border-red-600 bg-red-50',
    message: DASHBOARD.OVERDUE_CASE_MESSAGE,
  },
  at_risk_case: {
    tileName: DASHBOARD.AT_RISK,
    border:
      'border-b-2 border border-b-yellow-600 bg-white hover:border-yellow-600 hover:bg-yellow-50',
    selected:
      'border-b-2 border border-b-yellow-600 border-yellow-600 bg-yellow-50',
    message: DASHBOARD.AT_RISK_CASE_MESSAGE,
  },
  on_track_case: {
    tileName: DASHBOARD.ON_TRACK,
    border:
      'border-b-2 border border-b-green-600 bg-white hover:border-green-600 hover:bg-green-50',
    selected:
      'border-b-2 border border-b-green-600 border-green-600 bg-green-50',
    message: DASHBOARD.ON_TRACK_CASE_MESSAGE,
  },
  total_case: {
    tileName: DASHBOARD.TOTAL_CASES_TO_BE_PROCESSED,
    border:
      'border-b-2 border border-b-blue-600 bg-white hover:border-blue-600 hover:bg-blue-50',
    selected: 'border-b-2 border border-b-blue-600 border-blue-600 bg-blue-50',
    message: DASHBOARD.TOTAL_CASE_MESSAGE,
  },
  paused_case: {
    tileName: DASHBOARD.PAUSED_MI,
    border:
      'border-b-2 border border-b-blue-300 bg-white hover:border-blue-300 hover:bg-blue-50',
    selected: 'border-b-2 border border-b-blue-300 border-blue-300 bg-blue-50',
    message: DASHBOARD.PAUSED_CASE_MESSAGE,
  },
  unassigned_case: {
    tileName: DASHBOARD.UNASSIGNED,
    border:
      'border-b-2 border border-b-gray-600 bg-white hover:border-gray-600 hover:bg-gray-50',
    selected: 'border-b-2 border border-b-gray-600 border-gray-600 bg-gray-50',
    message: DASHBOARD.UNASSIGNED_CASE_MESSAGE,
  },
  all_cases: {
    tileName: DASHBOARD.ALL_CASES,
    border: 'border-2',
    message: DASHBOARD.ALL_CASES,
  },
  overdue_document: {
    tileName: DASHBOARD.OVERDUE,
    border:
      'border-b-2 border border-b-red-600 bg-white hover:border-red-600 hover:bg-red-50',
    selected: 'border-b-2 border border-b-red-600 border-red-600 bg-red-50',
    message: DASHBOARD.OVERDUE_DOCUMENT_MESSAGE,
  },
  at_risk_document: {
    tileName: DASHBOARD.AT_RISK,
    border:
      'border-b-2 border border-b-yellow-600 bg-white hover:border-yellow-600 hover:bg-yellow-50',
    selected:
      'border-b-2 border border-b-yellow-600 border-yellow-600 bg-yellow-50',
    message: DASHBOARD.AT_RISK_DOCUMENT_MESSAGE,
  },
  on_track_document: {
    tileName: DASHBOARD.ON_TRACK,
    border:
      'border-b-2 border border-b-green-600 bg-white hover:border-green-600 hover:bg-green-50',
    selected:
      'border-b-2 border border-b-green-600 border-green-600 bg-green-50',
    message: DASHBOARD.ON_TRACK_DOCUMENT_MESSAGE,
  },
  total_document: {
    tileName: DASHBOARD.DOCUMENTS_REVIEWED,
    border:
      'border-b-2 border border-b-blue-600 bg-white hover:border-blue-600 hover:bg-blue-50',
    selected: 'border-b-2 border border-b-blue-600 border-blue-600 bg-blue-50',
    message: DASHBOARD.TOTAL_DOCUMENT_MESSAGE,
  },
  unassigned_document: {
    tileName: DASHBOARD.UNASSIGNED,
    border:
      'border-b-2 border border-b-gray-600 bg-white hover:border-gray-600 hover:bg-gray-50',
    selected: 'border-b-2 border border-b-gray-600 border-gray-600 bg-gray-50',
    message: DASHBOARD.UNASSIGNED_DOCUMENT_MESSAGE,
  },
}

export const FIXED_TABS = [
  {
    label: correspondenceLogsPage.OUTBOUND,
    component: null,
  },
  {
    label: correspondenceLogsPage.INBOUND_FAX,
    component: null,
  },
  {
    label: correspondenceLogsPage.INBOUND_WEB,
    component: null,
  },
]

export const AWS_ENCRYPTION_KEY = 'a8F#kL92@pQx!7Zm$3TnV5wR'

export const INTAKE_STATUS = [
  DASHBOARD.OVERDUE,
  DASHBOARD.AT_RISK,
  DASHBOARD.ON_TRACK,
  DASHBOARD.PAUSED_MI,
]

export const STATUS_ID_NOT_NULL = [
  {
    field_name: 'status',
    operation: 'IS_NOT_NULL',
    values: [],
  },
  {
    field_name: 'id',
    operation: 'IS_NOT_NULL',
    values: [],
  },
]
